import React, { useEffect, useState } from 'react';
import {
  Plus, Send, CreditCard, X, Trash2, Euro, Eye, Printer,
  Ban, Download, FileText, Clock, Lock, History, FileDown, Bell,
  BarChart3, AlertTriangle, ArrowRightCircle, CheckCircle, XCircle, Edit3,
  Package, Search, TrendingUp, TrendingDown, ArrowRight, PieChart,
} from 'lucide-react';
import { invoicesApi, offersApi, clientsApi, casesApi, authApi, packagesApi, paymentsApi } from '../services/api';
import { useToast } from '../context/ToastContext';
import { SkeletonTable } from '../components/Skeleton';
import StripeCheckout from '../components/StripeCheckout';
import { useTour } from '../hooks/useTour';
import { invoicesTourSteps } from '../tours/invoicesTour';
import './Pages.css';

// Read current theme colors from CSS custom properties
function getThemeColors() {
  const s = getComputedStyle(document.documentElement);
  const primary = s.getPropertyValue('--primary').trim() || '#B07468';
  const primaryDark = s.getPropertyValue('--primary-dark').trim() || '#8E5B50';
  const border = s.getPropertyValue('--border').trim() || '#EBDDD6';
  // Lighter tint for table header backgrounds — 8% opacity of primary
  const r = parseInt(primary.slice(1, 3), 16), g = parseInt(primary.slice(3, 5), 16), b = parseInt(primary.slice(5, 7), 16);
  const headBg = `rgba(${r},${g},${b},0.07)`;
  const badgeBg = `rgba(${r},${g},${b},0.1)`;
  const badgeBorder = `rgba(${r},${g},${b},0.3)`;
  return { primary, primaryDark, border, headBg, badgeBg, badgeBorder };
}

// --- Shared Interfaces ---

interface InvoiceItem {
  id: string;
  description: string;
  quantity: number;
  unit: string;
  unit_price_eur: number;
  total_eur: number;
  tax_rate_percent: number;
}

interface Invoice {
  id: string;
  invoice_number: string;
  invoice_date: string;
  due_date: string;
  service_date: string | null;
  subject: string | null;
  intro_text: string | null;
  outro_text: string | null;
  subtotal_eur: number;
  tax_rate_percent: number;
  tax_amount_eur: number;
  total_eur: number;
  status: string;
  client_id: string | null;
  case_id: string | null;
  package_id: string | null;
  installment_number: number | null;
  items: InvoiceItem[];
  sent_at: string | null;
  paid_at: string | null;
  cancelled_at: string | null;
  offer_id: string | null;
  payment_terms: string | null;
  is_locked: boolean;
  is_cancellation: boolean;
  cancellation_of_id: string | null;
  cancelled_by_id: string | null;
  cancellation_reason: string | null;
  reminder_count?: number;
  last_reminder_sent?: string | null;
  // Mahnwesen v2
  dunning_level?: number;
  last_dunned_at?: string | null;
  next_dunning_date?: string | null;
  dunning_entries?: DunningEntry[];
  // Denormalisierte Felder
  package_name?: string | null;
  client_name?: string | null;
  total_paid?: number | null;
}

interface DunningEntry {
  id: string;
  invoice_id: string;
  level: string;
  level_number: number;
  fee: number;
  sent_at: string | null;
  sent_via: string;
  email_subject: string | null;
  deadline: string | null;
  note: string | null;
  created_at: string;
}

interface ClientPackageInfo {
  id: string;
  client_id: string;
  package_id: string;
  package_name: string | null;
  client_name: string | null;
  sessions_total: number;
  sessions_used: number;
  sessions_remaining: number;
  price_paid_eur: number;
  billing_method: string;
  installment_count: number | null;
  amount_invoiced: number;
  amount_paid: number;
  status: string;
}

interface Offer {
  id: string;
  offer_number: string;
  offer_date: string;
  valid_until: string;
  subject: string | null;
  intro_text: string | null;
  outro_text: string | null;
  subtotal_eur: number;
  tax_rate_percent: number;
  tax_amount_eur: number;
  total_eur: number;
  status: string;
  client_id: string | null;
  items: InvoiceItem[];
  sent_at: string | null;
  converted_to_invoice_id: string | null;
}

interface AuditEntry {
  id: string;
  action: string;
  description: string | null;
  timestamp: string;
}

interface MonthlyRevenue {
  month: string;
  revenue_eur: number;
}

interface Stats {
  total_revenue_eur: number;
  outstanding_eur: number;
  overdue_count: number;
  overdue_total_eur: number;
  draft_count: number;
  paid_count: number;
  monthly_revenue: MonthlyRevenue[];
}

interface Client {
  id: string;
  first_name: string;
  last_name: string;
  email?: string | null;
  phone?: string | null;
  address_line1?: string | null;
  zip_code?: string | null;
  city?: string | null;
}

interface CoachProfile {
  first_name: string;
  last_name: string;
  email: string;
  phone?: string | null;
  title?: string | null;
  business_name?: string | null;
  address_line1?: string | null;
  address_line2?: string | null;
  zip_code?: string | null;
  city?: string | null;
  country?: string | null;
  tax_number?: string | null;
  vat_id?: string | null;
  bank_name?: string | null;
  iban?: string | null;
  bic?: string | null;
  is_kleinunternehmer?: boolean;
}

const statusLabels: Record<string, string> = {
  draft: 'Entwurf',
  sent: 'Gesendet',
  paid: 'Bezahlt',
  partially_paid: 'Teilbezahlt',
  overdue: 'Überfällig',
  cancelled: 'Storniert',
  open: 'Offen',
};

const statusBadge: Record<string, string> = {
  draft: 'badge-new',
  sent: 'badge-ongoing',
  paid: 'badge-active',
  partially_paid: 'badge-ongoing',
  overdue: 'badge-paused',
  cancelled: 'badge-completed',
};

const offerStatusLabels: Record<string, string> = {
  entwurf: 'Entwurf',
  gesendet: 'Gesendet',
  angenommen: 'Angenommen',
  abgelehnt: 'Abgelehnt',
  abgelaufen: 'Abgelaufen',
};

const offerStatusBadge: Record<string, string> = {
  entwurf: 'badge-new',
  gesendet: 'badge-ongoing',
  angenommen: 'badge-active',
  abgelehnt: 'badge-paused',
  abgelaufen: 'badge-completed',
};

const dunningLevelLabels: Record<string, string> = {
  erinnerung: 'Zahlungserinnerung',
  mahnung_1: '1. Mahnung',
  mahnung_2: '2. Mahnung',
  mahnung_3: '3. und letzte Mahnung',
};

const dunningLevelKeys = ['erinnerung', 'mahnung_1', 'mahnung_2', 'mahnung_3'];

const unitLabels: Record<string, string> = {
  session: 'Sitzung',
  hour: 'Stunde',
  day: 'Tag',
  item: 'Stück',
  pauschal: 'Pauschal',
  month: 'Monat',
};

type TabKey = 'dashboard' | 'angebote' | 'rechnungen' | 'mahnungen';

export default function Invoices() {
  const { showToast } = useToast();
  useTour('invoices', invoicesTourSteps);
  const [activeTab, setActiveTab] = useState<TabKey>('dashboard');
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [total, setTotal] = useState(0);
  const [stats, setStats] = useState<Stats | null>(null);
  const [offers, setOffers] = useState<Offer[]>([]);
  const [overdueInvoices, setOverdueInvoices] = useState<Invoice[]>([]);
  const [showForm, setShowForm] = useState(false);
  const [editInvoice, setEditInvoice] = useState<Invoice | null>(null);
  const [showOfferForm, setShowOfferForm] = useState(false);
  const [editOffer, setEditOffer] = useState<Offer | null>(null);
  const [showPayment, setShowPayment] = useState<Invoice | null>(null);
  const [showStripeCheckout, setShowStripeCheckout] = useState<Invoice | null>(null);
  const [showCancel, setShowCancel] = useState<Invoice | null>(null);
  const [previewInvoice, setPreviewInvoice] = useState<Invoice | null>(null);
  const [previewOffer, setPreviewOffer] = useState<Offer | null>(null);
  const [filter, setFilter] = useState('');
  const [offerFilter, setOfferFilter] = useState('');
  const [offersTotal, setOffersTotal] = useState(0);
  const [clients, setClients] = useState<Client[]>([]);
  const [profile, setProfile] = useState<CoachProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showDunning, setShowDunning] = useState<Invoice | null>(null);
  const [actionLoading, setActionLoading] = useState<string | null>(null);

  const loadInvoices = async () => {
    setLoading(true);
    try {
      const invRes = await invoicesApi.list({
        status: filter || undefined,
        search: searchTerm || undefined,
      });
      setInvoices(invRes.data.items);
      setTotal(invRes.data.total);
    } catch { /* ignore */ }
    finally { setLoading(false); }
  };

  const loadStats = async () => {
    try {
      const statsRes = await invoicesApi.stats();
      setStats(statsRes.data);
    } catch { /* ignore */ }
  };

  const loadOffers = async () => {
    try {
      const res = await offersApi.list({ status: offerFilter || undefined });
      setOffers(res.data.items);
      setOffersTotal(res.data.total);
    } catch { /* ignore */ }
  };

  const loadOverdue = async () => {
    try {
      const res = await invoicesApi.overdue();
      setOverdueInvoices((res.data.items || []).sort((a: Invoice, b: Invoice) => a.due_date.localeCompare(b.due_date)));
    } catch { /* ignore */ }
  };

  const load = async () => {
    await Promise.all([loadInvoices(), loadStats(), loadOffers(), loadOverdue()]);
  };

  const loadProfile = () => {
    authApi.me().then((res) => setProfile(res.data)).catch(() => {});
  };

  useEffect(() => {
    load();
    clientsApi.list({ limit: 200 }).then((res) => setClients(res.data.items)).catch(() => {});
    loadProfile();
  }, []); // eslint-disable-line

  useEffect(() => { loadInvoices(); }, [filter, searchTerm]); // eslint-disable-line
  useEffect(() => { loadOffers(); }, [offerFilter]); // eslint-disable-line

  const handleSend = async (id: string) => {
    const inv = invoices.find((i) => i.id === id);
    const clientEmail = inv?.client_id ? clients.find((c) => c.id === inv.client_id)?.email : null;
    const emailNote = clientEmail
      ? `\n\nDie Rechnung wird per E-Mail an ${clientEmail} gesendet.`
      : '\n\n(Kein E-Mail-Versand — Klient hat keine E-Mail-Adresse)';
    if (!window.confirm(`Rechnung als gesendet markieren? Die Rechnung wird damit gesperrt und kann nicht mehr bearbeitet werden (GoBD).${emailNote}`)) return;
    setActionLoading(id);
    try {
      await invoicesApi.send(id);
      showToast('Rechnung gesendet und gesperrt', 'success');
      load();
    } catch {
      showToast('Fehler beim Senden der Rechnung', 'error');
    } finally {
      setActionLoading(null);
    }
  };

  const handleSendReminder = async (id: string, invoiceList?: Invoice[]) => {
    const list = invoiceList || invoices;
    const inv = list.find((i) => i.id === id);
    const level = (inv as any)?.reminder_count ? (inv as any).reminder_count + 1 : 1;
    const levelText = level === 1 ? 'Zahlungserinnerung' : level === 2 ? '2. Mahnung' : '3. Mahnung';
    if (!window.confirm(`${levelText} per E-Mail senden?`)) return;
    setActionLoading(id);
    try {
      await invoicesApi.sendReminder(id);
      showToast(`${levelText} gesendet`, 'success');
      load();
    } catch (err: any) {
      const detail = err.response?.data?.detail;
      showToast(typeof detail === 'string' ? detail : 'Fehler beim Senden der Erinnerung', 'error');
    } finally {
      setActionLoading(null);
    }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('Entwurf wirklich löschen?')) return;
    setActionLoading(id);
    try {
      await invoicesApi.delete(id);
      showToast('Entwurf gelöscht', 'success');
      load();
    } catch { showToast('Löschen fehlgeschlagen', 'error'); }
    finally { setActionLoading(null); }
  };

  const handleExport = async (type: 'datev' | 'csv') => {
    try {
      const res = type === 'datev'
        ? await invoicesApi.exportDatev()
        : await invoicesApi.exportCsv();
      const blob = new Blob([res.data], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = type === 'datev'
        ? `DATEV_Buchungsstapel_${new Date().toISOString().split('T')[0]}.csv`
        : `Rechnungen_${new Date().toISOString().split('T')[0]}.csv`;
      a.click();
      URL.revokeObjectURL(url);
      showToast(`${type === 'datev' ? 'DATEV' : 'CSV'}-Export heruntergeladen`, 'success');
    } catch {
      showToast('Export fehlgeschlagen', 'error');
    }
  };

  const handleOfferExport = async () => {
    try {
      const res = await offersApi.exportCsv();
      const blob = new Blob([res.data], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Angebote_${new Date().toISOString().split('T')[0]}.csv`;
      a.click();
      URL.revokeObjectURL(url);
      showToast('Angebote-CSV heruntergeladen', 'success');
    } catch {
      showToast('Export fehlgeschlagen', 'error');
    }
  };

  const getClientName = (clientId: string | null) => {
    if (!clientId) return null;
    const c = clients.find((cl) => cl.id === clientId);
    return c ? `${c.first_name} ${c.last_name}` : null;
  };

  const fmt = (n: number) =>
    n.toLocaleString('de-DE', { style: 'currency', currency: 'EUR' });

  const fmtDate = (d: string) =>
    new Date(d).toLocaleDateString('de-DE');

  const daysOverdue = (dueDate: string) => {
    const due = new Date(dueDate);
    const today = new Date();
    return Math.floor((today.getTime() - due.getTime()) / (1000 * 60 * 60 * 24));
  };

  // --- Offer handlers ---
  const handleOfferSend = async (id: string) => {
    if (!window.confirm('Angebot als gesendet markieren?')) return;
    setActionLoading(`offer-${id}`);
    try {
      await offersApi.send(id);
      showToast('Angebot als gesendet markiert', 'success');
      load();
    } catch { showToast('Fehler beim Senden', 'error'); }
    finally { setActionLoading(null); }
  };

  const handleOfferConvert = async (id: string) => {
    if (!window.confirm('Angebot in Rechnung umwandeln?')) return;
    setActionLoading(`offer-${id}`);
    try {
      await offersApi.convert(id);
      showToast('Angebot in Rechnung umgewandelt', 'success');
      load();
      setActiveTab('rechnungen');
    } catch { showToast('Fehler bei der Umwandlung', 'error'); }
    finally { setActionLoading(null); }
  };

  const handleOfferDelete = async (id: string) => {
    if (!window.confirm('Angebot wirklich löschen?')) return;
    setActionLoading(`offer-${id}`);
    try {
      await offersApi.delete(id);
      showToast('Angebot gelöscht', 'success');
      load();
    } catch { showToast('Löschen fehlgeschlagen', 'error'); }
    finally { setActionLoading(null); }
  };

  const handleOfferStatusChange = async (id: string, status: string) => {
    const label = status === 'angenommen' ? 'Angebot angenommen' : status === 'abgelehnt' ? 'Angebot abgelehnt' : `Status: ${status}`;
    setActionLoading(`offer-${id}`);
    try {
      await offersApi.changeStatus(id, status);
      showToast(label, 'success');
      load();
    } catch { showToast('Status-Änderung fehlgeschlagen', 'error'); }
    finally { setActionLoading(null); }
  };

  return (
    <div>
      <div className="flex-between mb-24">
        <div>
          <h1>Rechnungen & Angebote</h1>
          <p className="text-muted text-sm">Rechnungen, Angebote und Mahnwesen verwalten</p>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="pill-tabs mb-24">
        <button className={`pill-tab ${activeTab === 'dashboard' ? 'pill-tab-active' : ''}`} onClick={() => setActiveTab('dashboard')}>
          <BarChart3 size={15} /> Dashboard
        </button>
        <button className={`pill-tab ${activeTab === 'angebote' ? 'pill-tab-active' : ''}`} onClick={() => setActiveTab('angebote')}>
          <FileText size={15} /> Angebote
          {offersTotal > 0 && <span className="pill-tab-badge">{offersTotal}</span>}
        </button>
        <button className={`pill-tab ${activeTab === 'rechnungen' ? 'pill-tab-active' : ''}`} onClick={() => setActiveTab('rechnungen')}>
          <Euro size={15} /> Rechnungen
          {total > 0 && <span className="pill-tab-badge">{total}</span>}
        </button>
        <button className={`pill-tab ${activeTab === 'mahnungen' ? 'pill-tab-active' : ''}`} data-tour="invoice-dunning" onClick={() => setActiveTab('mahnungen')}>
          <AlertTriangle size={15} /> Mahnungen
          {overdueInvoices.length > 0 && <span className="pill-tab-badge pill-tab-badge-danger">{overdueInvoices.length}</span>}
        </button>
      </div>

      {/* === DASHBOARD TAB === */}
      {activeTab === 'dashboard' && (
        <>
          {stats && (() => {
            // Trend-Berechnung
            const mr = stats.monthly_revenue || [];
            const lastMonthRev = mr.length >= 1 ? mr[mr.length - 1].revenue_eur : 0;
            const prevMonthRev = mr.length >= 2 ? mr[mr.length - 2].revenue_eur : 0;
            const trendPct = prevMonthRev > 0 ? ((lastMonthRev - prevMonthRev) / prevMonthRev) * 100 : 0;
            const trendUp = trendPct >= 0;

            // Counts
            const paidCount = stats.paid_count || 0;
            const sentCount = invoices.filter(i => i.status === 'sent').length;
            const cancelledCount = invoices.filter(i => i.status === 'cancelled').length;

            // Paket-Typ-Donut
            const pkgMap: Record<string, number> = {};
            invoices.filter(i => ['sent', 'paid', 'partially_paid', 'overdue'].includes(i.status)).forEach(inv => {
              const key = inv.package_name || 'Einzelrechnung';
              pkgMap[key] = (pkgMap[key] || 0) + inv.total_eur;
            });
            const pkgData = Object.entries(pkgMap).sort((a, b) => b[1] - a[1]).slice(0, 6);

            // Letzte 5 Rechnungen
            const recentInvoices = [...invoices]
              .sort((a, b) => b.invoice_date.localeCompare(a.invoice_date))
              .slice(0, 5);

            return (
              <>
                {/* Hero KPI + 3 Small KPIs */}
                <div className="kpi-grid-hero">
                  <div className="hero-kpi" onClick={() => { setFilter('paid'); setActiveTab('rechnungen'); }} style={{ cursor: 'pointer' }}>
                    <div className="hero-kpi-label">Umsatz (bezahlt)</div>
                    <div className="hero-kpi-value">{fmt(stats.total_revenue_eur)}</div>
                    {mr.length >= 2 && (
                      <span className={`hero-kpi-trend ${trendUp ? 'hero-kpi-trend-up' : 'hero-kpi-trend-down'}`}>
                        {trendUp ? <TrendingUp size={13} /> : <TrendingDown size={13} />}
                        {trendUp ? '+' : ''}{trendPct.toFixed(1)} % ggü. Vormonat
                      </span>
                    )}
                  </div>
                  <div className="kpi-small" onClick={() => { setFilter('open'); setActiveTab('rechnungen'); }}>
                    <div className="kpi-small-icon" style={{ background: 'rgba(217, 119, 6, 0.1)', color: 'var(--accent, #d97706)' }}>
                      <Clock size={18} />
                    </div>
                    <div className="kpi-small-value">{fmt(stats.outstanding_eur)}</div>
                    <div className="kpi-small-label">Ausstehend</div>
                  </div>
                  <div className="kpi-small" onClick={() => setActiveTab('mahnungen')}>
                    <div className="kpi-small-icon" style={{ background: overdueInvoices.length > 0 ? 'rgba(220, 38, 38, 0.1)' : 'rgba(34, 197, 94, 0.1)', color: overdueInvoices.length > 0 ? 'var(--danger)' : 'var(--success)' }}>
                      <AlertTriangle size={18} />
                    </div>
                    <div className="kpi-small-value" style={{ color: overdueInvoices.length > 0 ? 'var(--danger)' : undefined }}>{overdueInvoices.length}</div>
                    <div className="kpi-small-label">Überfällig</div>
                  </div>
                  <div className="kpi-small" onClick={() => { setFilter('draft'); setActiveTab('rechnungen'); }}>
                    <div className="kpi-small-icon" style={{ background: 'rgba(var(--primary-rgb, 176, 116, 104), 0.1)', color: 'var(--primary)' }}>
                      <FileText size={18} />
                    </div>
                    <div className="kpi-small-value">{stats.draft_count}</div>
                    <div className="kpi-small-label">Entwürfe</div>
                  </div>
                </div>

                {/* Status Strip */}
                <div className="status-strip">
                  <div className="status-chip" onClick={() => { setFilter('paid'); setActiveTab('rechnungen'); }}>
                    <span className="status-chip-dot" style={{ background: 'var(--success)' }} />
                    <div>
                      <div className="status-chip-count">{paidCount}</div>
                      <div className="status-chip-label">Bezahlt</div>
                    </div>
                  </div>
                  <div className="status-chip" onClick={() => { setFilter('open'); setActiveTab('rechnungen'); }}>
                    <span className="status-chip-dot" style={{ background: 'var(--accent, #d97706)' }} />
                    <div>
                      <div className="status-chip-count">{sentCount}</div>
                      <div className="status-chip-label">Gesendet</div>
                    </div>
                  </div>
                  <div className="status-chip" onClick={() => { setFilter('draft'); setActiveTab('rechnungen'); }}>
                    <span className="status-chip-dot" style={{ background: 'var(--text-muted)' }} />
                    <div>
                      <div className="status-chip-count">{stats.draft_count}</div>
                      <div className="status-chip-label">Entwürfe</div>
                    </div>
                  </div>
                  <div className="status-chip" onClick={() => { setFilter('cancelled'); setActiveTab('rechnungen'); }}>
                    <span className="status-chip-dot" style={{ background: 'var(--border)' }} />
                    <div>
                      <div className="status-chip-count">{cancelledCount}</div>
                      <div className="status-chip-label">Storniert</div>
                    </div>
                  </div>
                </div>

                {/* Charts Row */}
                <div className="charts-row">
                  {/* Revenue Bar Chart */}
                  {mr.length > 0 && <RevenueChart data={mr} />}

                  {/* Donut Chart */}
                  {pkgData.length > 0 && <DonutChart data={pkgData} />}
                </div>

                {/* Recent Invoices */}
                {recentInvoices.length > 0 && (
                  <div className="recent-invoices">
                    <div className="recent-header">
                      <span className="chart-title"><Euro size={16} /> Letzte Rechnungen</span>
                      <button className="btn btn-sm btn-secondary" onClick={() => setActiveTab('rechnungen')}>
                        Alle anzeigen <ArrowRight size={14} />
                      </button>
                    </div>
                    {recentInvoices.map((inv) => {
                      const paid = inv.total_paid || 0;
                      const paidPct = inv.total_eur > 0 ? Math.min(100, Math.round(paid / inv.total_eur * 100)) : 0;
                      const name = inv.client_name || getClientName(inv.client_id);
                      const initials = name ? name.split(' ').map(w => w[0]).join('').slice(0, 2) : '?';
                      return (
                        <div key={inv.id} className="recent-row" style={{ cursor: 'pointer' }} onClick={() => { loadProfile(); setPreviewInvoice(inv); }}>
                          <div className="client-avatar client-avatar-sm" style={{ background: 'var(--primary)' }}>{initials}</div>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ fontWeight: 600, fontSize: '0.88rem', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{name || 'Unbekannt'}</div>
                            <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>{inv.invoice_number} · {fmtDate(inv.invoice_date)}</div>
                          </div>
                          <div style={{ textAlign: 'right', minWidth: 100 }}>
                            <div style={{ fontWeight: 600, fontSize: '0.88rem' }}>{fmt(inv.total_eur)}</div>
                            {inv.status !== 'draft' && inv.status !== 'cancelled' && (
                              <div className="pay-progress">
                                <div className="pay-progress-bar">
                                  <div className="pay-progress-fill" style={{ width: `${paidPct}%`, background: paidPct >= 100 ? 'var(--success)' : 'var(--primary)' }} />
                                </div>
                              </div>
                            )}
                          </div>
                          <span className={`badge ${statusBadge[inv.status] || ''}`} style={{ fontSize: '0.72rem' }}>
                            {statusLabels[inv.status] || inv.status}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                )}
              </>
            );
          })()}
        </>
      )}

      {/* === ANGEBOTE TAB === */}
      {activeTab === 'angebote' && (
        <>
          <div className="flex-between mb-16">
            <div className="flex gap-8">
              <button className="btn btn-secondary" onClick={handleOfferExport}>
                <Download size={16} /> CSV
              </button>
            </div>
            <button className="btn btn-primary" onClick={() => setShowOfferForm(true)}>
              <Plus size={18} /> Neues Angebot
            </button>
          </div>

          <div className="category-chips mb-16">
            {['', 'entwurf', 'gesendet', 'angenommen', 'abgelehnt', 'abgelaufen'].map((s) => (
              <button
                key={s}
                className={`chip ${offerFilter === s ? 'chip-active' : ''}`}
                onClick={() => setOfferFilter(s)}
              >
                {s === '' ? 'Alle' : offerStatusLabels[s]}
              </button>
            ))}
          </div>

          {offers.length === 0 ? (
            <div className="empty-state card">
              <p>Noch keine Angebote vorhanden.</p>
              <button className="btn btn-primary mt-16" onClick={() => setShowOfferForm(true)}>
                <Plus size={18} /> Erstes Angebot erstellen
              </button>
            </div>
          ) : (
            <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
              <table className="table">
                <thead>
                  <tr>
                    <th>Nr.</th>
                    <th>Klient</th>
                    <th>Betreff</th>
                    <th>Datum</th>
                    <th>Gültig bis</th>
                    <th>Betrag</th>
                    <th>Status</th>
                    <th>Aktionen</th>
                  </tr>
                </thead>
                <tbody>
                  {offers.map((off) => (
                    <tr key={off.id} style={{ cursor: 'pointer' }} onClick={() => { loadProfile(); setPreviewOffer(off); }}>
                      <td style={{ fontFamily: 'monospace' }}>{off.offer_number}</td>
                      <td>{getClientName(off.client_id) || '—'}</td>
                      <td style={{ fontWeight: 500 }}>{off.subject || '—'}</td>
                      <td className="text-light">{fmtDate(off.offer_date)}</td>
                      <td className="text-light">{fmtDate(off.valid_until)}</td>
                      <td style={{ fontWeight: 600 }}>{fmt(off.total_eur)}</td>
                      <td>
                        <span className={`badge ${offerStatusBadge[off.status] || ''}`}>
                          {offerStatusLabels[off.status] || off.status}
                        </span>
                      </td>
                      <td onClick={(e) => e.stopPropagation()}>
                        <div className="flex gap-8">
                          {off.status === 'entwurf' && (
                            <>
                              <button className="btn btn-sm btn-secondary" onClick={() => setEditOffer(off)} disabled={actionLoading === `offer-${off.id}`} data-tip="Angebot bearbeiten">
                                <Edit3 size={14} />
                              </button>
                              <button className="btn btn-sm btn-primary" onClick={() => handleOfferSend(off.id)} disabled={actionLoading === `offer-${off.id}`} data-tip="Angebot als gesendet markieren">
                                <Send size={14} />
                              </button>
                              <button className="btn btn-sm btn-danger" onClick={() => handleOfferDelete(off.id)} disabled={actionLoading === `offer-${off.id}`} data-tip="Angebot löschen">
                                <Trash2 size={14} />
                              </button>
                            </>
                          )}
                          {off.status === 'gesendet' && (
                            <>
                              <button className="btn btn-sm btn-accent" onClick={() => handleOfferStatusChange(off.id, 'angenommen')} disabled={actionLoading === `offer-${off.id}`} data-tip="Angebot als angenommen markieren">
                                <CheckCircle size={14} />
                              </button>
                              <button className="btn btn-sm btn-danger" onClick={() => handleOfferStatusChange(off.id, 'abgelehnt')} disabled={actionLoading === `offer-${off.id}`} data-tip="Angebot als abgelehnt markieren">
                                <XCircle size={14} />
                              </button>
                            </>
                          )}
                          {(off.status === 'angenommen' || off.status === 'gesendet') && !off.converted_to_invoice_id && (
                            <button className="btn btn-sm btn-primary" onClick={() => handleOfferConvert(off.id)} disabled={actionLoading === `offer-${off.id}`} data-tip="Rechnung aus Angebot erstellen">
                              <ArrowRightCircle size={14} />
                            </button>
                          )}
                          {off.converted_to_invoice_id && (
                            <span className="text-sm text-muted" style={{ padding: '4px 0' }}>Rechnung erstellt</span>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}

      {/* === RECHNUNGEN TAB === */}
      {activeTab === 'rechnungen' && (
        <>
          <div className="flex-between mb-16">
            <div className="flex gap-8" style={{ alignItems: 'center' }} data-tour="invoice-filters">
              <div style={{ position: 'relative' }}>
                <Search size={16} style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', opacity: 0.4 }} />
                <input
                  type="text"
                  placeholder="Suche (Nr., Betreff)..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  style={{ paddingLeft: 32, width: 220 }}
                />
              </div>
              <div className="category-chips">
                {['', 'draft', 'open', 'paid', 'cancelled'].map((s) => (
                  <button
                    key={s}
                    className={`chip ${filter === s ? 'chip-active' : ''}`}
                    onClick={() => setFilter(s)}
                  >
                    {s === '' ? 'Alle' : s === 'open' ? 'Offene' : statusLabels[s]}
                  </button>
                ))}
              </div>
            </div>
            {(!filter || filter === 'draft' || filter === 'open') && (
              <button className="btn btn-primary" data-tour="invoice-add-btn" onClick={() => setShowForm(true)}>
                <Plus size={18} /> Neue Rechnung
              </button>
            )}
          </div>

          {loading ? (
            <div className="card" style={{ padding: 16 }}>
              <SkeletonTable />
            </div>
          ) : invoices.length === 0 ? (
            <div className="empty-state card">
              {filter ? (
                <p>Keine Rechnungen mit Status „{statusLabels[filter] || filter}" vorhanden.</p>
              ) : (
                <>
                  <p>Noch keine Rechnungen vorhanden.</p>
                  <button className="btn btn-primary mt-16" onClick={() => setShowForm(true)}>
                    <Plus size={18} /> Erste Rechnung erstellen
                  </button>
                </>
              )}
            </div>
          ) : (
            <>
              <div className="card" style={{ padding: 0, overflow: 'hidden' }} data-tour="invoice-list">
                <table className="table">
                  <thead>
                    <tr>
                      <th>Nr.</th>
                      <th>Klient</th>
                      <th>Paket</th>
                      <th>Betreff</th>
                      <th>Datum</th>
                      <th>Fällig</th>
                      <th>Betrag</th>
                      <th>Bezahlt</th>
                      <th>Status</th>
                      <th>Aktionen</th>
                    </tr>
                  </thead>
                  <tbody>
                    {invoices.map((inv) => {
                      const paid = inv.total_paid || 0;
                      const paidPct = inv.total_eur > 0 ? Math.min(100, Math.round(paid / inv.total_eur * 100)) : 0;
                      const clientName = inv.client_name || getClientName(inv.client_id);
                      const initials = clientName ? clientName.split(' ').map((w: string) => w[0]).join('').slice(0, 2) : '?';
                      const days = daysOverdue(inv.due_date);
                      const isDue = inv.status !== 'draft' && inv.status !== 'paid' && inv.status !== 'cancelled' && !inv.is_cancellation;
                      const dueClass = isDue && days > 30 ? 'due-overdue-heavy' : isDue && days > 0 ? 'due-overdue' : isDue && days > -7 ? 'due-soon' : '';
                      return (
                      <tr key={inv.id} style={{ ...(inv.is_cancellation ? { opacity: 0.6, fontStyle: 'italic' as const } : {}), cursor: 'pointer' }} onClick={() => { loadProfile(); setPreviewInvoice(inv); }}>
                        <td style={{ fontFamily: 'monospace' }}>
                          {inv.invoice_number}
                          {inv.is_locked && <Lock size={12} style={{ marginLeft: 4, opacity: 0.4 }} />}
                        </td>
                        <td>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            <div className="client-avatar client-avatar-sm" style={{ background: 'var(--primary)' }}>{initials}</div>
                            <span>{clientName || '—'}</span>
                          </div>
                        </td>
                        <td>
                          {inv.package_name ? (
                            <span className="text-sm" style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                              <Package size={12} style={{ opacity: 0.5 }} />
                              {inv.package_name}
                            </span>
                          ) : '—'}
                        </td>
                        <td style={{ fontWeight: 500 }}>{inv.subject || '—'}</td>
                        <td className="text-light">{fmtDate(inv.invoice_date)}</td>
                        <td className={dueClass}>{fmtDate(inv.due_date)}</td>
                        <td style={{ fontWeight: 600, color: inv.is_cancellation ? 'var(--danger)' : undefined }}>
                          {fmt(inv.total_eur)}
                        </td>
                        <td>
                          {inv.status !== 'draft' && inv.status !== 'cancelled' && !inv.is_cancellation ? (
                            <div className="pay-progress">
                              <div className="text-sm" style={{ marginBottom: 2 }}>
                                {fmt(paid)} / {fmt(inv.total_eur)}
                              </div>
                              <div className="pay-progress-bar">
                                <div className="pay-progress-fill" style={{ width: `${paidPct}%`, background: paidPct >= 100 ? 'var(--success)' : 'var(--primary)' }} />
                              </div>
                            </div>
                          ) : '—'}
                        </td>
                        <td>
                          <span className={`badge ${statusBadge[inv.status] || ''}`}>
                            {statusLabels[inv.status] || inv.status}
                          </span>
                        </td>
                        <td onClick={(e) => e.stopPropagation()}>
                          <div className="flex gap-8">
                            {inv.status === 'draft' && (
                              <>
                                <button className="btn btn-sm btn-secondary" onClick={() => setEditInvoice(inv)} disabled={actionLoading === inv.id} data-tip="Rechnung bearbeiten">
                                  <Edit3 size={14} />
                                </button>
                                <button className="btn btn-sm btn-primary" onClick={() => handleSend(inv.id)} disabled={actionLoading === inv.id} data-tip="Rechnung versenden und sperren (GoBD)">
                                  <Send size={14} />
                                </button>
                              </>
                            )}
                            {['sent', 'partially_paid', 'overdue'].includes(inv.status) && (
                              <>
                                <button className="btn btn-sm btn-accent" onClick={() => setShowPayment(inv)} disabled={actionLoading === inv.id} data-tip="Eingehende Zahlung erfassen">
                                  <CreditCard size={14} />
                                </button>
                                <button className="btn btn-sm btn-secondary" onClick={() => handleSendReminder(inv.id)} disabled={actionLoading === inv.id} data-tip="Zahlungserinnerung per E-Mail senden">
                                  <Bell size={14} />
                                </button>
                                <button className="btn btn-sm btn-danger" onClick={() => setShowCancel(inv)} disabled={actionLoading === inv.id} data-tip="Stornorechnung erstellen (GoBD-konform)">
                                  <Ban size={14} />
                                </button>
                              </>
                            )}
                            {inv.status === 'paid' && !inv.is_cancellation && !inv.cancelled_by_id && (
                              <button className="btn btn-sm btn-danger" onClick={() => setShowCancel(inv)} disabled={actionLoading === inv.id} data-tip="Stornorechnung erstellen (GoBD-konform)">
                                <Ban size={14} />
                              </button>
                            )}
                            {inv.status === 'draft' && (
                              <button className="btn btn-sm btn-danger" onClick={() => handleDelete(inv.id)} disabled={actionLoading === inv.id} data-tip="Entwurf unwiderruflich löschen">
                                <Trash2 size={14} />
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              {/* Export Bar */}
              <div className="export-bar">
                <span className="export-bar-label"><Download size={14} style={{ marginRight: 6, verticalAlign: 'middle' }} />Rechnungen exportieren</span>
                <div className="flex gap-8">
                  <button className="btn btn-sm btn-secondary" onClick={() => handleExport('csv')}>
                    <Download size={14} /> CSV
                  </button>
                  <button className="btn btn-sm btn-secondary" onClick={() => handleExport('datev')}>
                    <FileText size={14} /> DATEV
                  </button>
                </div>
              </div>
            </>
          )}
        </>
      )}

      {/* === MAHNUNGEN TAB === */}
      {activeTab === 'mahnungen' && (
        <>
          {/* KPI Cards */}
          {stats && (
            <div className="dunning-kpi-grid">
              <div className="dunning-kpi">
                <div className="dunning-kpi-icon" style={{ background: overdueInvoices.length > 0 ? 'rgba(220, 38, 38, 0.1)' : 'rgba(34, 197, 94, 0.1)', color: overdueInvoices.length > 0 ? 'var(--danger)' : 'var(--success)' }}>
                  <AlertTriangle size={20} />
                </div>
                <div>
                  <div className="dunning-kpi-value" style={{ color: overdueInvoices.length > 0 ? 'var(--danger)' : undefined }}>{overdueInvoices.length}</div>
                  <div className="dunning-kpi-label">Überfällige Rechnungen</div>
                </div>
              </div>
              <div className="dunning-kpi">
                <div className="dunning-kpi-icon" style={{ background: 'rgba(217, 119, 6, 0.1)', color: 'var(--accent, #d97706)' }}>
                  <Euro size={20} />
                </div>
                <div>
                  <div className="dunning-kpi-value">{fmt(stats.overdue_total_eur || 0)}</div>
                  <div className="dunning-kpi-label">Offener Gesamtbetrag</div>
                </div>
              </div>
              <div className="dunning-kpi">
                <div className="dunning-kpi-icon" style={{ background: 'rgba(var(--primary-rgb, 176, 116, 104), 0.1)', color: 'var(--primary)' }}>
                  <Clock size={20} />
                </div>
                <div>
                  <div className="dunning-kpi-value">{overdueInvoices.filter(i => (i.dunning_level || 0) === 0).length}</div>
                  <div className="dunning-kpi-label">Noch nicht gemahnt</div>
                </div>
              </div>
              <div className="dunning-kpi">
                <div className="dunning-kpi-icon" style={{ background: 'rgba(220, 38, 38, 0.08)', color: 'var(--danger)' }}>
                  <Bell size={20} />
                </div>
                <div>
                  <div className="dunning-kpi-value">{overdueInvoices.filter(i => daysOverdue(i.due_date) > 30).length}</div>
                  <div className="dunning-kpi-label">&gt; 30 Tage überfällig</div>
                </div>
              </div>
            </div>
          )}

          {overdueInvoices.length === 0 ? (
            <div className="card dunning-empty">
              <div className="dunning-empty-icon">
                <CheckCircle size={28} style={{ color: 'var(--success)' }} />
              </div>
              <h3 style={{ marginBottom: 4 }}>Alles bezahlt!</h3>
              <p className="text-muted">Keine überfälligen Rechnungen vorhanden.</p>
            </div>
          ) : (
            <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
              <table className="table">
                <thead>
                  <tr>
                    <th>Nr.</th>
                    <th>Klient</th>
                    <th>Fällig seit</th>
                    <th>Tage</th>
                    <th>Betrag</th>
                    <th>Restbetrag</th>
                    <th>Mahnstufe</th>
                    <th>Aktionen</th>
                  </tr>
                </thead>
                <tbody>
                  {overdueInvoices.map((inv) => {
                    const days = daysOverdue(inv.due_date);
                    const urgency = days > 30 ? 'var(--danger)' : days > 14 ? '#e67e22' : 'var(--accent)';
                    const paid = inv.total_paid || 0;
                    const remaining = Math.max(0, inv.total_eur - paid);
                    const level = inv.dunning_level || 0;
                    const levelLabel = level === 0 ? 'Keine' : level === 1 ? 'Erinnerung' : `${level}. Mahnung`;
                    const levelColor = level === 0 ? '#999' : level <= 1 ? 'var(--accent)' : level === 2 ? '#e67e22' : 'var(--danger)';
                    return (
                      <tr key={inv.id} style={{ cursor: 'pointer' }} onClick={() => { loadProfile(); setPreviewInvoice(inv); }}>
                        <td style={{ fontFamily: 'monospace' }}>{inv.invoice_number}</td>
                        <td>{inv.client_name || getClientName(inv.client_id) || '—'}</td>
                        <td className="text-light">{fmtDate(inv.due_date)}</td>
                        <td>
                          <span style={{ color: urgency, fontWeight: 600 }}>{days}</span>
                        </td>
                        <td style={{ fontWeight: 500 }}>{fmt(inv.total_eur)}</td>
                        <td style={{ fontWeight: 600, color: remaining > 0 ? 'var(--danger)' : 'var(--success)' }}>
                          {fmt(remaining)}
                        </td>
                        <td>
                          <span style={{ display: 'inline-block', padding: '2px 10px', borderRadius: 12, fontSize: '0.75rem', fontWeight: 600, background: `${levelColor}15`, color: levelColor, border: `1px solid ${levelColor}40` }}>
                            {levelLabel}
                          </span>
                        </td>
                        <td onClick={(e) => e.stopPropagation()}>
                          <div className="flex gap-8">
                            <button className="btn btn-sm btn-accent" onClick={() => setShowPayment(inv)} data-tip="Zahlung erfassen">
                              <CreditCard size={14} />
                            </button>
                            <button
                              className="btn btn-sm btn-primary"
                              onClick={() => setShowDunning(inv)}
                              data-tip="Mahnung erstellen"
                              disabled={level >= 4}
                            >
                              <Bell size={14} /> Mahnen
                            </button>
                            <button className="btn btn-sm btn-secondary" onClick={() => { loadProfile(); setPreviewInvoice(inv); }} data-tip="Vorschau">
                              <Eye size={14} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}

      {/* Modals */}
      {showForm && (
        <InvoiceForm
          onClose={() => setShowForm(false)}
          onSaved={load}
          onPreview={(inv) => { setShowForm(false); loadProfile(); setPreviewInvoice(inv); }}
        />
      )}
      {editInvoice && (
        <InvoiceForm
          editInvoice={editInvoice}
          onClose={() => setEditInvoice(null)}
          onSaved={load}
          onPreview={(inv) => { setEditInvoice(null); loadProfile(); setPreviewInvoice(inv); }}
        />
      )}
      {showOfferForm && (
        <OfferForm
          onClose={() => setShowOfferForm(false)}
          onSaved={load}
        />
      )}
      {editOffer && (
        <OfferForm
          editOffer={editOffer}
          onClose={() => setEditOffer(null)}
          onSaved={load}
        />
      )}
      {showPayment && <PaymentForm invoice={showPayment} onClose={() => setShowPayment(null)} onSaved={load} onStripe={(inv) => { setShowPayment(null); setShowStripeCheckout(inv); }} />}
      {showStripeCheckout && (
        <StripeCheckout
          invoice={{ ...showStripeCheckout, total_paid: showStripeCheckout.total_paid || 0 }}
          onClose={() => setShowStripeCheckout(null)}
          onSuccess={() => { setShowStripeCheckout(null); load(); }}
          showToast={showToast}
        />
      )}
      {showCancel && <CancelForm invoice={showCancel} onClose={() => setShowCancel(null)} onSaved={load} />}
      {showDunning && (
        <DunningModal
          invoice={showDunning}
          onClose={() => setShowDunning(null)}
          onSaved={load}
        />
      )}
      {previewInvoice && profile && (
        <InvoicePreview
          invoice={previewInvoice}
          profile={profile}
          clientName={getClientName(previewInvoice.client_id)}
          client={clients.find((c) => c.id === previewInvoice.client_id) || null}
          onClose={() => setPreviewInvoice(null)}
          onSend={handleSend}
          onPayment={(inv) => setShowPayment(inv)}
          onCancel={(inv) => setShowCancel(inv)}
          onEdit={(inv) => setEditInvoice(inv)}
          onDelete={handleDelete}
          onReminder={(id) => handleSendReminder(id)}
          onStripeCheckout={(inv) => setShowStripeCheckout(inv)}
        />
      )}
      {previewOffer && profile && (
        <OfferPreview
          offer={previewOffer}
          profile={profile}
          clientName={getClientName(previewOffer.client_id)}
          client={clients.find((c) => c.id === previewOffer.client_id) || null}
          onClose={() => setPreviewOffer(null)}
          onConvert={previewOffer.status !== 'abgelehnt' && previewOffer.status !== 'abgelaufen' && !previewOffer.converted_to_invoice_id
            ? () => { handleOfferConvert(previewOffer.id); setPreviewOffer(null); }
            : undefined}
        />
      )}
    </div>
  );
}

// === Revenue Chart (SVG, redesigned) ===

function RevenueChart({ data }: { data: MonthlyRevenue[] }) {
  const maxRevenue = Math.max(...data.map((d) => d.revenue_eur), 1);
  const chartHeight = 200;
  const barWidth = 44;
  const gap = 8;
  const leftPad = 50;
  const chartWidth = data.length * (barWidth + gap) - gap + leftPad + 10;

  const monthLabels: Record<string, string> = {
    '01': 'Jan', '02': 'Feb', '03': 'Mär', '04': 'Apr',
    '05': 'Mai', '06': 'Jun', '07': 'Jul', '08': 'Aug',
    '09': 'Sep', '10': 'Okt', '11': 'Nov', '12': 'Dez',
  };

  const fmtShort = (n: number) => {
    if (n >= 1000) return `${(n / 1000).toFixed(1)}k`;
    return n.toFixed(0);
  };

  // Y-axis grid lines (4 steps)
  const gridSteps = 4;
  const peakIdx = data.reduce((max, d, i) => d.revenue_eur > data[max].revenue_eur ? i : max, 0);
  const currentMonthStr = `${new Date().getFullYear()}-${String(new Date().getMonth() + 1).padStart(2, '0')}`;

  return (
    <div className="chart-card">
      <div className="chart-header">
        <span className="chart-title"><BarChart3 size={16} /> Monatsumsatz (Brutto)</span>
      </div>
      <div style={{ overflowX: 'auto' }}>
        <svg width={chartWidth} height={chartHeight + 36} style={{ display: 'block' }}>
          {/* Grid lines + Y-axis labels */}
          {Array.from({ length: gridSteps + 1 }).map((_, i) => {
            const val = (maxRevenue / gridSteps) * (gridSteps - i);
            const yPos = (i / gridSteps) * chartHeight;
            return (
              <g key={i}>
                <line x1={leftPad - 4} y1={yPos} x2={chartWidth} y2={yPos} stroke="var(--border-light, #eee)" strokeWidth="1" />
                <text x={leftPad - 8} y={yPos + 4} textAnchor="end" fontSize="9" fill="var(--text-muted, #999)">{fmtShort(val)}</text>
              </g>
            );
          })}
          {/* Bars */}
          {data.map((d, i) => {
            const revenue = d.revenue_eur;
            const barHeight = maxRevenue > 0 ? (revenue / maxRevenue) * chartHeight : 0;
            const x = leftPad + i * (barWidth + gap);
            const y = chartHeight - barHeight;
            const month = d.month.split('-')[1];
            const isPeak = i === peakIdx && d.revenue_eur > 0;
            const isCurrent = d.month === currentMonthStr;
            const barColor = isPeak ? '#22c55e' : isCurrent ? 'var(--primary-dark)' : 'var(--primary)';
            return (
              <g key={d.month}>
                <rect x={x} y={y} width={barWidth} height={barHeight} rx={4} fill={d.revenue_eur > 0 ? barColor : 'var(--border-light, #e5e5e5)'} opacity={d.revenue_eur > 0 ? 0.9 : 0.3} />
                {d.revenue_eur > 0 && (
                  <text x={x + barWidth / 2} y={y - 6} textAnchor="middle" fontSize="9" fill="var(--text-light, #666)" fontWeight="600">{fmtShort(revenue)}</text>
                )}
                <text x={x + barWidth / 2} y={chartHeight + 16} textAnchor="middle" fontSize="9" fill={isCurrent ? 'var(--primary)' : 'var(--text-muted, #999)'} fontWeight={isCurrent ? '700' : '400'}>{monthLabels[month] || month}</text>
              </g>
            );
          })}
        </svg>
      </div>
    </div>
  );
}

// === Donut Chart (SVG) ===

const DONUT_COLORS = ['var(--primary)', '#3b82f6', '#22c55e', '#f59e0b', '#ef4444', '#8b5cf6'];

function DonutChart({ data }: { data: [string, number][] }) {
  const total = data.reduce((sum, [, v]) => sum + v, 0);
  const radius = 70;
  const cx = 90;
  const cy = 90;
  const strokeWidth = 24;
  const circumference = 2 * Math.PI * radius;
  let offset = 0;

  const fmt = (n: number) => n.toLocaleString('de-DE', { style: 'currency', currency: 'EUR' });

  return (
    <div className="chart-card">
      <div className="chart-header">
        <span className="chart-title"><PieChart size={16} /> Nach Paket-Typ</span>
      </div>
      <div style={{ display: 'flex', justifyContent: 'center' }}>
        <svg width={180} height={180} viewBox="0 0 180 180">
          {total === 0 ? (
            <circle cx={cx} cy={cy} r={radius} fill="none" stroke="var(--border-light, #eee)" strokeWidth={strokeWidth} />
          ) : (
            data.map(([label, value], i) => {
              const pct = value / total;
              const dashLen = pct * circumference;
              const dashOffset = -offset;
              offset += dashLen;
              return (
                <circle
                  key={label}
                  cx={cx} cy={cy} r={radius}
                  fill="none"
                  stroke={DONUT_COLORS[i % DONUT_COLORS.length]}
                  strokeWidth={strokeWidth}
                  strokeDasharray={`${dashLen} ${circumference - dashLen}`}
                  strokeDashoffset={dashOffset}
                  transform={`rotate(-90 ${cx} ${cy})`}
                  style={{ transition: 'stroke-dasharray 0.5s' }}
                />
              );
            })
          )}
          <text x={cx} y={cy - 6} textAnchor="middle" fontSize="14" fontWeight="700" fill="var(--text)">{fmt(total)}</text>
          <text x={cx} y={cy + 10} textAnchor="middle" fontSize="9" fill="var(--text-muted)">Gesamt</text>
        </svg>
      </div>
      <div className="donut-legend">
        {data.map(([label, value], i) => (
          <div key={label} className="donut-legend-item">
            <span className="donut-legend-dot" style={{ background: DONUT_COLORS[i % DONUT_COLORS.length] }} />
            <span style={{ color: 'var(--text-light)' }}>{label}</span>
            <span className="donut-legend-value">{fmt(value)}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

// === Offer Preview (like InvoicePreview) ===

function OfferPreview({
  offer,
  profile,
  clientName,
  client,
  onClose,
  onConvert,
}: {
  offer: Offer;
  profile: CoachProfile;
  clientName: string | null;
  client: Client | null;
  onClose: () => void;
  onConvert?: () => void;
}) {
  const fmt = (n: number) => n.toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  const fmtDate = (d: string) => new Date(d).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' });

  const coachName = profile.business_name || `${profile.first_name} ${profile.last_name}`;
  const hasAddress = profile.address_line1 || profile.zip_code;
  const hasBankDetails = profile.iban;
  const hasTax = profile.tax_number || profile.vat_id;

  const senderParts = [coachName];
  if (profile.address_line1) senderParts.push(profile.address_line1);
  if (profile.zip_code || profile.city) senderParts.push(`${profile.zip_code || ''} ${profile.city || ''}`.trim());
  const senderLine = senderParts.join(' · ');

  const handlePrint = () => {
    const printContent = document.getElementById('offer-print-area');
    if (!printContent) return;
    const w = window.open('', '_blank');
    if (!w) return;
    const tc = getThemeColors();
    w.document.write(`<!DOCTYPE html><html><head><title>Angebot ${offer.offer_number}</title>
<style>
@page { margin: 20mm 15mm 25mm 20mm; size: A4; }
* { margin: 0; padding: 0; box-sizing: border-box; }
body { font-family: 'Helvetica Neue', Helvetica, Arial, sans-serif; font-size: 9.5pt; color: #333; line-height: 1.5; }
.inv-accent { height: 4px; background: linear-gradient(90deg, ${tc.primary}, ${tc.primaryDark}); margin-bottom: 30px; }
.inv-header { display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 8px; }
.inv-logo-area .company-name { font-size: 16pt; font-weight: 700; color: ${tc.primary}; letter-spacing: 0.5px; }
.inv-logo-area .company-subtitle { font-size: 8.5pt; color: #888; margin-top: 2px; }
.inv-badge { display: inline-block; padding: 4px 14px; border-radius: 4px; font-size: 8pt; font-weight: 600; letter-spacing: 1.5px; text-transform: uppercase; }
.inv-badge-angebot { background: ${tc.badgeBg}; color: ${tc.primary}; border: 1px solid ${tc.badgeBorder}; }
.inv-sender-line { font-size: 7pt; color: #999; letter-spacing: 0.3px; border-bottom: 1px solid #e5e5e5; padding-bottom: 4px; margin-bottom: 8px; margin-top: 28px; }
.inv-address-meta { display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 32px; gap: 40px; }
.inv-recipient { flex: 1; }
.inv-recipient .name { font-size: 10.5pt; font-weight: 600; color: #222; }
.inv-recipient .address-line { font-size: 9.5pt; color: #555; line-height: 1.7; }
.inv-details { width: 220px; flex-shrink: 0; }
.inv-details table { width: 100%; border-collapse: collapse; }
.inv-details td { padding: 3px 0; font-size: 9pt; vertical-align: top; }
.inv-details td:first-child { color: #888; padding-right: 12px; white-space: nowrap; }
.inv-details td:last-child { color: #333; font-weight: 500; text-align: right; }
.inv-subject { margin-bottom: 20px; font-size: 10pt; }
.inv-subject strong { color: ${tc.primary}; }
.inv-greeting { font-size: 9.5pt; color: #555; margin-bottom: 16px; white-space: pre-line; }
.inv-table { width: 100%; border-collapse: collapse; margin-bottom: 4px; }
.inv-table thead { background: ${tc.headBg}; }
.inv-table th { padding: 10px 12px; text-align: left; font-size: 8pt; font-weight: 600; text-transform: uppercase; letter-spacing: 0.6px; color: ${tc.primary}; border-bottom: 2px solid ${tc.border}; }
.inv-table th.right { text-align: right; }
.inv-table td { padding: 11px 12px; font-size: 9.5pt; border-bottom: 1px solid #f0ece6; color: #444; }
.inv-table td.right { text-align: right; font-variant-numeric: tabular-nums; }
.inv-table td.pos { color: #999; width: 36px; }
.inv-table tbody tr:last-child td { border-bottom: 2px solid ${tc.border}; }
.inv-totals { display: flex; justify-content: flex-end; margin-bottom: 24px; }
.inv-totals-inner { width: 260px; }
.inv-totals-row { display: flex; justify-content: space-between; padding: 5px 12px; font-size: 9.5pt; color: #666; }
.inv-totals-row.sub { border-top: 1px solid #e5e5e5; margin-top: 4px; padding-top: 8px; }
.inv-totals-row.final { background: ${tc.primary}; color: white; font-weight: 700; font-size: 11pt; padding: 10px 12px; border-radius: 4px; margin-top: 6px; }
.inv-outro { font-size: 9.5pt; color: #555; margin-bottom: 24px; white-space: pre-line; }
.inv-validity { background: ${tc.headBg}; border-radius: 6px; padding: 14px 16px; margin-bottom: 24px; font-size: 9pt; color: #666; }
.inv-validity strong { color: ${tc.primary}; }
.inv-footer { border-top: 2px solid ${tc.border}; padding-top: 16px; margin-top: 40px; }
.inv-footer-grid { display: grid; grid-template-columns: 1fr 1fr 1fr; gap: 24px; font-size: 8pt; line-height: 1.8; color: #777; }
.inv-footer-grid .col-title { font-size: 7.5pt; font-weight: 700; text-transform: uppercase; letter-spacing: 0.8px; color: ${tc.primary}; margin-bottom: 4px; }
.inv-footer-note { text-align: center; margin-top: 16px; font-size: 7.5pt; color: #bbb; letter-spacing: 0.3px; }
@media print {
  body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
  .inv-table thead { background: ${tc.headBg} !important; }
  .inv-totals-row.final { background: ${tc.primary} !important; color: white !important; }
}
</style></head><body>${printContent.innerHTML}</body></html>`);
    w.document.close();
    w.focus();
    setTimeout(() => { w.print(); w.close(); }, 400);
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="invoice-preview-modal" onClick={(e) => e.stopPropagation()}>
        <div className="invoice-preview-toolbar">
          <div>
            <h3 style={{ marginBottom: 2 }}>Angebotsvorschau</h3>
            <span className="text-sm text-muted">
              {offer.offer_number}
              <span style={{ marginLeft: 8 }} className={`badge ${offerStatusBadge[offer.status] || ''}`}>
                {offerStatusLabels[offer.status] || offer.status}
              </span>
            </span>
          </div>
          <div className="flex gap-8">
            {onConvert && (
              <button className="btn btn-accent btn-sm" data-tip="Rechnung aus diesem Angebot erstellen" onClick={onConvert}>
                <ArrowRightCircle size={14} /> In Rechnung umwandeln
              </button>
            )}
            <button className="btn btn-secondary btn-sm" data-tip="Druckansicht öffnen" onClick={handlePrint}>
              <Printer size={14} /> Drucken
            </button>
            <button className="btn-icon" data-tip="Schließen" onClick={onClose}><X size={20} /></button>
          </div>
        </div>

        <div className="invoice-preview-content" id="offer-print-area">
          {/* Accent Bar */}
          <div className="inv-accent" />

          {/* Header */}
          <div className="inv-header">
            <div className="inv-logo-area">
              <div className="company-name">{coachName}</div>
              {profile.title && <div className="company-subtitle">{profile.title}</div>}
            </div>
            <div>
              <span className="inv-badge inv-badge-angebot">Angebot</span>
            </div>
          </div>

          {/* Sender line */}
          <div className="inv-sender-line">{senderLine}</div>

          {/* Address + Meta */}
          <div className="inv-address-meta">
            <div className="inv-recipient">
              {clientName ? (
                <>
                  <div className="name">{clientName}</div>
                  {client?.address_line1 && <div className="address-line">{client.address_line1}</div>}
                  {(client?.zip_code || client?.city) && (
                    <div className="address-line">{client?.zip_code} {client?.city}</div>
                  )}
                  {client?.email && <div className="address-line">{client.email}</div>}
                </>
              ) : (
                <div className="name" style={{ color: '#999', fontStyle: 'italic' }}>Kein Empfänger zugeordnet</div>
              )}
            </div>

            <div className="inv-details">
              <table>
                <tbody>
                  <tr><td>Angebotsnr.</td><td>{offer.offer_number}</td></tr>
                  <tr><td>Datum</td><td>{fmtDate(offer.offer_date)}</td></tr>
                  <tr><td>Gültig bis</td><td>{fmtDate(offer.valid_until)}</td></tr>
                  {hasTax && (
                    <>
                      {profile.tax_number && <tr><td>Steuernr.</td><td>{profile.tax_number}</td></tr>}
                      {profile.vat_id && <tr><td>USt-IdNr.</td><td>{profile.vat_id}</td></tr>}
                    </>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* Subject */}
          {offer.subject && (
            <div className="inv-subject">
              <strong>Betreff:</strong> {offer.subject}
            </div>
          )}

          {/* Intro Text */}
          {offer.intro_text && (
            <div className="inv-greeting">{offer.intro_text}</div>
          )}
          {!offer.intro_text && (
            <div className="inv-greeting">
              {clientName
                ? `Sehr ${clientName.startsWith('Frau') ? 'geehrte' : 'geehrte/r'} ${clientName},`
                : 'Sehr geehrte Damen und Herren,'
              }{'\n'}
              gerne unterbreite ich Ihnen folgendes Angebot:
            </div>
          )}

          {/* Items Table */}
          <table className="inv-table">
            <thead>
              <tr>
                <th>Pos.</th>
                <th>Beschreibung</th>
                <th className="right">Menge</th>
                <th className="right">Einheit</th>
                <th className="right">Einzelpreis</th>
                <th className="right">Gesamt</th>
              </tr>
            </thead>
            <tbody>
              {offer.items.map((item, i) => (
                <tr key={item.id}>
                  <td className="pos">{i + 1}</td>
                  <td>{item.description}</td>
                  <td className="right">{item.quantity % 1 === 0 ? item.quantity : fmt(item.quantity)}</td>
                  <td className="right">{unitLabels[item.unit] || item.unit}</td>
                  <td className="right">{fmt(item.unit_price_eur)} &euro;</td>
                  <td className="right">{fmt(item.total_eur)} &euro;</td>
                </tr>
              ))}
            </tbody>
          </table>

          {/* Totals */}
          <div className="inv-totals">
            <div className="inv-totals-inner">
              <div className="inv-totals-row">
                <span>Zwischensumme (netto)</span>
                <span>{fmt(offer.subtotal_eur)} &euro;</span>
              </div>
              {offer.tax_rate_percent > 0 ? (
                <div className="inv-totals-row sub">
                  <span>USt. {fmt(offer.tax_rate_percent)}%</span>
                  <span>{fmt(offer.tax_amount_eur)} &euro;</span>
                </div>
              ) : profile.is_kleinunternehmer ? (
                <div className="inv-totals-row sub" style={{ fontSize: '8pt', color: '#999' }}>
                  <span>Kein USt.-Ausweis (Kleinunternehmer gem. § 19 UStG)</span>
                </div>
              ) : (
                <div className="inv-totals-row sub">
                  <span>USt. 0,00%</span>
                  <span>0,00 &euro;</span>
                </div>
              )}
              <div className="inv-totals-row final" style={{ background: 'var(--primary)' }}>
                <span>Angebotssumme</span>
                <span>{fmt(offer.total_eur)} &euro;</span>
              </div>
            </div>
          </div>

          {/* Outro Text */}
          {offer.outro_text && (
            <div className="inv-outro">{offer.outro_text}</div>
          )}

          {/* Validity info */}
          <div className="inv-validity">
            <strong>Gültigkeit:</strong> Dieses Angebot ist gültig bis zum {fmtDate(offer.valid_until)}.
            {offer.converted_to_invoice_id && (
              <span> Dieses Angebot wurde in eine Rechnung umgewandelt.</span>
            )}
          </div>

          {/* Footer */}
          <div className="inv-footer">
            <div className="inv-footer-grid">
              <div>
                <div className="col-title">Kontakt</div>
                <div>{coachName}</div>
                {hasAddress && (
                  <>
                    {profile.address_line1 && <div>{profile.address_line1}</div>}
                    {profile.address_line2 && <div>{profile.address_line2}</div>}
                    {(profile.zip_code || profile.city) && <div>{profile.zip_code} {profile.city}</div>}
                  </>
                )}
                <div>{profile.email}</div>
                {profile.phone && <div>Tel. {profile.phone}</div>}
              </div>
              <div>
                <div className="col-title">Bankverbindung</div>
                {hasBankDetails ? (
                  <>
                    {profile.bank_name && <div>{profile.bank_name}</div>}
                    <div>IBAN: {profile.iban}</div>
                    {profile.bic && <div>BIC: {profile.bic}</div>}
                  </>
                ) : (
                  <div style={{ color: '#ccc', fontStyle: 'italic' }}>Unter Einstellungen hinterlegen</div>
                )}
              </div>
              <div>
                <div className="col-title">Steuerdaten</div>
                {hasTax ? (
                  <>
                    {profile.tax_number && <div>Steuernr.: {profile.tax_number}</div>}
                    {profile.vat_id && <div>USt-IdNr.: {profile.vat_id}</div>}
                  </>
                ) : (
                  <div style={{ color: '#ccc', fontStyle: 'italic' }}>Unter Einstellungen hinterlegen</div>
                )}
              </div>
            </div>
            <div className="inv-footer-note">
              Erstellt mit CoachDesk365
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// === Offer Create Form ===

function OfferForm({
  onClose,
  onSaved,
  editOffer,
}: {
  onClose: () => void;
  onSaved: () => Promise<void> | void;
  editOffer?: Offer;
}) {
  const isEdit = !!editOffer;
  const [clients, setClients] = useState<Client[]>([]);
  const [clientId, setClientId] = useState(editOffer?.client_id || '');
  const [subject, setSubject] = useState(editOffer?.subject || '');
  const [introText, setIntroText] = useState(editOffer?.intro_text || '');
  const [outroText, setOutroText] = useState(editOffer?.outro_text || '');
  const [validUntil, setValidUntil] = useState(editOffer?.valid_until || '');
  const [taxRate, setTaxRate] = useState(editOffer ? String(editOffer.tax_rate_percent) : '19');
  const [items, setItems] = useState<ItemRow[]>(
    editOffer && editOffer.items.length > 0
      ? editOffer.items.map((it) => ({
          description: it.description,
          quantity: String(it.quantity),
          unit: it.unit,
          unit_price_eur: String(it.unit_price_eur),
        }))
      : [{ description: '', quantity: '1', unit: 'session', unit_price_eur: '' }]
  );
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    clientsApi.list({ limit: 200 }).then((res) => setClients(res.data.items));
  }, []);

  const updateItem = (i: number, field: string, value: string) => {
    const copy = [...items];
    (copy[i] as any)[field] = value;
    setItems(copy);
  };

  const addItem = () => setItems([...items, { description: '', quantity: '1', unit: 'session', unit_price_eur: '' }]);
  const removeItem = (i: number) => items.length > 1 && setItems(items.filter((_, idx) => idx !== i));

  const subtotal = items.reduce((sum, it) => {
    const q = parseFloat(it.quantity) || 0;
    const p = parseFloat(it.unit_price_eur) || 0;
    return sum + q * p;
  }, 0);
  const tax = subtotal * (parseFloat(taxRate) || 0) / 100;
  const total = subtotal + tax;

  const fmtEur = (n: number) => n.toLocaleString('de-DE', { style: 'currency', currency: 'EUR' });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setError('');

    for (let i = 0; i < items.length; i++) {
      if (!items[i].description.trim()) {
        setError(`Position ${i + 1}: Beschreibung fehlt`);
        setSaving(false);
        return;
      }
      if (!items[i].unit_price_eur || isNaN(parseFloat(items[i].unit_price_eur))) {
        setError(`Position ${i + 1}: Preis fehlt`);
        setSaving(false);
        return;
      }
    }

    try {
      const payload = {
        client_id: clientId || undefined,
        subject: subject || undefined,
        intro_text: introText || undefined,
        outro_text: outroText || undefined,
        valid_until: validUntil || undefined,
        tax_rate_percent: parseFloat(taxRate),
        items: items.map((it) => ({
          description: it.description.trim(),
          quantity: parseFloat(it.quantity) || 1,
          unit: it.unit,
          unit_price_eur: parseFloat(it.unit_price_eur),
          tax_rate_percent: parseFloat(taxRate),
        })),
      };
      if (isEdit) {
        await offersApi.update(editOffer!.id, payload);
      } else {
        await offersApi.create(payload);
      }
      Promise.resolve(onSaved()).catch(() => {});
      onClose();
    } catch (err: any) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : 'Fehler beim Speichern');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal modal-lg" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>{isEdit ? 'Angebot bearbeiten' : 'Neues Angebot'}</h2>
          <button className="btn-icon" onClick={onClose}><X size={20} /></button>
        </div>
        {error && <div className="auth-error" style={{ margin: '0 24px' }}>{error}</div>}
        <form onSubmit={handleSubmit}>
          <div className="form-row">
            <div className="form-group">
              <label>Klient</label>
              <select value={clientId} onChange={(e) => setClientId(e.target.value)}>
                <option value="">— Klient wählen —</option>
                {clients.map((c) => (
                  <option key={c.id} value={c.id}>{c.first_name} {c.last_name}</option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label>MwSt. (%)</label>
              <select value={taxRate} onChange={(e) => setTaxRate(e.target.value)}>
                <option value="19">19% (Standard)</option>
                <option value="7">7% (ermäßigt)</option>
                <option value="0">0% (befreit)</option>
              </select>
            </div>
          </div>
          <div className="form-row">
            <div className="form-group" style={{ flex: 2 }}>
              <label>Betreff</label>
              <input value={subject} onChange={(e) => setSubject(e.target.value)} placeholder="z.B. Coaching-Paket 10 Sitzungen" />
            </div>
            <div className="form-group" style={{ flex: 1 }}>
              <label>Gültig bis</label>
              <input type="date" value={validUntil} onChange={(e) => setValidUntil(e.target.value)} />
            </div>
          </div>
          <div className="form-group">
            <label>Einleitungstext</label>
            <textarea
              value={introText}
              onChange={(e) => setIntroText(e.target.value)}
              placeholder="Vielen Dank für Ihr Interesse an meinem Coaching-Angebot..."
              rows={2}
            />
          </div>

          <h4 className="mb-8 mt-16">Positionen</h4>
          {items.map((item, i) => (
            <div className="invoice-item-row" key={i}>
              <input
                placeholder="Beschreibung *"
                value={item.description}
                onChange={(e) => updateItem(i, 'description', e.target.value)}
                style={{ flex: 3 }}
              />
              <input
                type="number"
                placeholder="Anz."
                value={item.quantity}
                onChange={(e) => updateItem(i, 'quantity', e.target.value)}
                min={0.5}
                step={0.5}
                style={{ flex: 0.7 }}
              />
              <select
                value={item.unit}
                onChange={(e) => updateItem(i, 'unit', e.target.value)}
                style={{ flex: 1 }}
              >
                <option value="session">Sitzung</option>
                <option value="hour">Stunde</option>
                <option value="day">Tag</option>
                <option value="pauschal">Pauschal</option>
                <option value="month">Monat</option>
                <option value="item">Stück</option>
              </select>
              <input
                type="number"
                placeholder="Preis (€) *"
                value={item.unit_price_eur}
                onChange={(e) => updateItem(i, 'unit_price_eur', e.target.value)}
                min={0}
                step={0.01}
                style={{ flex: 1 }}
              />
              {items.length > 1 && (
                <button type="button" className="btn-icon" onClick={() => removeItem(i)}>
                  <Trash2 size={16} />
                </button>
              )}
            </div>
          ))}
          <button type="button" className="btn btn-secondary btn-sm mt-8" onClick={addItem}>
            <Plus size={14} /> Position hinzufügen
          </button>

          <div className="form-group mt-16">
            <label>Schlusstext</label>
            <textarea
              value={outroText}
              onChange={(e) => setOutroText(e.target.value)}
              placeholder="Ich freue mich auf Ihre Rückmeldung..."
              rows={2}
            />
          </div>

          <div className="invoice-totals mt-16">
            <div className="total-row"><span>Netto</span><span>{fmtEur(subtotal)}</span></div>
            <div className="total-row"><span>MwSt. ({taxRate}%)</span><span>{fmtEur(tax)}</span></div>
            <div className="total-row total-final"><span>Gesamt</span><span>{fmtEur(total)}</span></div>
          </div>

          <div className="modal-footer">
            <button type="button" className="btn btn-secondary" onClick={onClose}>Abbrechen</button>
            <button type="submit" className="btn btn-primary" disabled={saving}>
              {saving ? 'Speichern...' : isEdit ? 'Angebot speichern' : 'Angebot erstellen'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// --- Invoice Create/Edit Form ---

interface ItemRow {
  description: string;
  quantity: string;
  unit: string;
  unit_price_eur: string;
}

function InvoiceForm({
  onClose,
  onSaved,
  onPreview,
  editInvoice,
}: {
  onClose: () => void;
  onSaved: () => Promise<void> | void;
  onPreview: (inv: Invoice) => void;
  editInvoice?: Invoice;
}) {
  const isEdit = !!editInvoice;
  const [clients, setClients] = useState<Client[]>([]);
  const [clientId, setClientId] = useState(editInvoice?.client_id || '');
  const [caseId, setCaseId] = useState(editInvoice?.case_id || '');
  const [clientCases, setClientCases] = useState<{ id: string; title: string; case_type: string }[]>([]);
  const [subject, setSubject] = useState(editInvoice?.subject || '');
  const [introText, setIntroText] = useState(editInvoice?.intro_text || '');
  const [outroText, setOutroText] = useState(editInvoice?.outro_text || '');
  const [serviceDate, setServiceDate] = useState(editInvoice?.service_date || '');
  const [taxRate, setTaxRate] = useState(editInvoice ? String(editInvoice.tax_rate_percent) : '19');
  const [packageId, setPackageId] = useState(editInvoice?.package_id || '');
  const [clientPackages, setClientPackages] = useState<ClientPackageInfo[]>([]);
  const [items, setItems] = useState<ItemRow[]>(
    editInvoice && editInvoice.items.length > 0
      ? editInvoice.items.map((it) => ({
          description: it.description,
          quantity: String(it.quantity),
          unit: it.unit,
          unit_price_eur: String(it.unit_price_eur),
        }))
      : [{ description: '', quantity: '1', unit: 'session', unit_price_eur: '' }]
  );
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    clientsApi.list({ limit: 200 }).then((res) => setClients(res.data.items));
  }, []);

  // Fälle + aktive Pakete laden, wenn Klient geändert wird
  useEffect(() => {
    if (clientId) {
      casesApi.list({ client_id: clientId }).then((res: any) => {
        const cases = (res.data.items || res.data || []).map((c: any) => ({
          id: c.id,
          title: c.title || c.case_number || 'Unbenannt',
          case_type: c.case_type || 'individual',
        }));
        setClientCases(cases);
        // Auto-select wenn nur 1 Fall existiert
        if (cases.length === 1 && !caseId) setCaseId(cases[0].id);
      }).catch(() => setClientCases([]));
      packagesApi.activeForClient(clientId).then((res: any) => {
        setClientPackages(res.data || []);
      }).catch(() => setClientPackages([]));
    } else {
      setClientCases([]);
      setCaseId('');
      setClientPackages([]);
      setPackageId('');
    }
  }, [clientId]);

  const selectedPackage = clientPackages.find((p) => p.id === packageId);

  const fillFromPackage = (cp: ClientPackageInfo) => {
    setPackageId(cp.id);
    const pricePerSession = cp.price_paid_eur / cp.sessions_total;
    const sessionsToInvoice = cp.billing_method === 'per_usage'
      ? cp.sessions_used
      : cp.sessions_total;
    setItems([{
      description: `Coaching-Paket: ${cp.package_name || 'Paket'}`,
      quantity: String(sessionsToInvoice),
      unit: 'session',
      unit_price_eur: String(pricePerSession.toFixed(2)),
    }]);
    setSubject(`Coaching-Paket: ${cp.package_name || 'Paket'}`);
  };

  const updateItem = (i: number, field: string, value: string) => {
    const copy = [...items];
    (copy[i] as any)[field] = value;
    setItems(copy);
  };

  const addItem = () => setItems([...items, { description: '', quantity: '1', unit: 'session', unit_price_eur: '' }]);
  const removeItem = (i: number) => items.length > 1 && setItems(items.filter((_, idx) => idx !== i));

  const subtotal = items.reduce((sum, it) => {
    const q = parseFloat(it.quantity) || 0;
    const p = parseFloat(it.unit_price_eur) || 0;
    return sum + q * p;
  }, 0);
  const tax = subtotal * (parseFloat(taxRate) || 0) / 100;
  const total = subtotal + tax;

  const fmtEur = (n: number) => n.toLocaleString('de-DE', { style: 'currency', currency: 'EUR' });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setError('');

    for (let i = 0; i < items.length; i++) {
      if (!items[i].description.trim()) {
        setError(`Position ${i + 1}: Beschreibung fehlt`);
        setSaving(false);
        return;
      }
      if (!items[i].unit_price_eur || isNaN(parseFloat(items[i].unit_price_eur))) {
        setError(`Position ${i + 1}: Preis fehlt`);
        setSaving(false);
        return;
      }
      if (!items[i].quantity || isNaN(parseFloat(items[i].quantity))) {
        setError(`Position ${i + 1}: Anzahl fehlt`);
        setSaving(false);
        return;
      }
    }

    try {
      const payload = {
        client_id: clientId || undefined,
        case_id: caseId || undefined,
        package_id: packageId || undefined,
        subject: subject || undefined,
        intro_text: introText || undefined,
        outro_text: outroText || undefined,
        service_date: serviceDate || undefined,
        tax_rate_percent: parseFloat(taxRate),
        items: items.map((it) => ({
          description: it.description.trim(),
          quantity: parseFloat(it.quantity),
          unit: it.unit,
          unit_price_eur: parseFloat(it.unit_price_eur),
          tax_rate_percent: parseFloat(taxRate),
        })),
      };
      let res;
      if (isEdit) {
        res = await invoicesApi.update(editInvoice!.id, payload);
      } else {
        res = await invoicesApi.create(payload);
      }
      Promise.resolve(onSaved()).catch(() => {});
      onPreview(res.data);
    } catch (err: any) {
      const detail = err.response?.data?.detail;
      const status = err.response?.status;
      const msg = typeof detail === 'string' ? detail
        : Array.isArray(detail) ? detail.map((d: any) => d.msg).join(', ')
        : status === 500 ? 'Serverfehler — bitte versuche es erneut'
        : 'Fehler beim Speichern';
      setError(msg);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal modal-lg" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>{isEdit ? 'Rechnung bearbeiten' : 'Neue Rechnung'}</h2>
          <button className="btn-icon" onClick={onClose}><X size={20} /></button>
        </div>
        {error && <div className="auth-error" style={{ margin: '0 24px' }}>{error}</div>}
        <form onSubmit={handleSubmit}>
          <div className="form-row">
            <div className="form-group">
              <label>Klient *</label>
              <select value={clientId} onChange={(e) => setClientId(e.target.value)}>
                <option value="">— Klient wählen —</option>
                {clients.map((c) => (
                  <option key={c.id} value={c.id}>{c.first_name} {c.last_name}</option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label>Fall</label>
              <select value={caseId} onChange={(e) => setCaseId(e.target.value)}>
                <option value="">— Kein Fall —</option>
                {clientCases.map((c) => (
                  <option key={c.id} value={c.id}>{c.title} ({c.case_type})</option>
                ))}
              </select>
            </div>
          </div>
          <div className="form-row">
            <div className="form-group">
              <label>MwSt. (%)</label>
              <select value={taxRate} onChange={(e) => setTaxRate(e.target.value)}>
                <option value="19">19% (Standard)</option>
                <option value="7">7% (ermäßigt)</option>
                <option value="0">0% (befreit)</option>
              </select>
            </div>
          </div>
          {/* Paket-Check: Info-Banner wenn Klient aktive Pakete hat */}
          {clientId && clientPackages.length > 0 && (
            <div style={{
              background: 'rgba(var(--primary-rgb, 176,116,104), 0.08)',
              border: '1px solid var(--border)',
              borderRadius: 8,
              padding: '12px 16px',
              marginBottom: 16,
            }}>
              <div style={{ fontWeight: 600, marginBottom: 8, display: 'flex', alignItems: 'center', gap: 6 }}>
                <Package size={16} /> Aktive Pakete
              </div>
              {clientPackages.map((cp) => (
                <div key={cp.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
                  <span className="text-sm">
                    {cp.package_name} ({cp.sessions_used}/{cp.sessions_total} Sitzungen, {cp.amount_paid.toLocaleString('de-DE', { style: 'currency', currency: 'EUR' })} bezahlt)
                  </span>
                  <button
                    type="button"
                    className="btn btn-sm btn-primary"
                    onClick={() => fillFromPackage(cp)}
                    style={{ marginLeft: 8 }}
                  >
                    Aus Paket abrechnen
                  </button>
                </div>
              ))}
              {packageId && (
                <button
                  type="button"
                  className="btn btn-sm btn-secondary mt-8"
                  onClick={() => setPackageId('')}
                >
                  Unabhängige Rechnung
                </button>
              )}
            </div>
          )}

          <div className="form-group">
            <label>Betreff *</label>
            <input value={subject} onChange={(e) => setSubject(e.target.value)} placeholder="z.B. Coaching-Session Februar 2026" required />
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Leistungszeitraum</label>
              <input value={serviceDate} onChange={(e) => setServiceDate(e.target.value)} placeholder="z.B. Januar – Februar 2026" />
            </div>
          </div>

          <h4 className="mb-8 mt-16">Positionen</h4>
          {items.map((item, i) => (
            <div className="invoice-item-row" key={i}>
              <input
                placeholder="Beschreibung *"
                value={item.description}
                onChange={(e) => updateItem(i, 'description', e.target.value)}
                style={{ flex: 3 }}
              />
              <input
                type="number"
                placeholder="Anz."
                value={item.quantity}
                onChange={(e) => updateItem(i, 'quantity', e.target.value)}
                min={0.5}
                step={0.5}
                style={{ flex: 0.7 }}
              />
              <select
                value={item.unit}
                onChange={(e) => updateItem(i, 'unit', e.target.value)}
                style={{ flex: 1 }}
              >
                <option value="session">Sitzung</option>
                <option value="hour">Stunde</option>
                <option value="day">Tag</option>
                <option value="pauschal">Pauschal</option>
                <option value="month">Monat</option>
                <option value="item">Stück</option>
              </select>
              <input
                type="number"
                placeholder="Preis (€) *"
                value={item.unit_price_eur}
                onChange={(e) => updateItem(i, 'unit_price_eur', e.target.value)}
                min={0}
                step={0.01}
                style={{ flex: 1 }}
              />
              {items.length > 1 && (
                <button type="button" className="btn-icon" onClick={() => removeItem(i)}>
                  <Trash2 size={16} />
                </button>
              )}
            </div>
          ))}
          <button type="button" className="btn btn-secondary btn-sm mt-8" onClick={addItem}>
            <Plus size={14} /> Position hinzufügen
          </button>

          <div className="invoice-totals mt-16">
            <div className="total-row"><span>Netto</span><span>{fmtEur(subtotal)}</span></div>
            <div className="total-row"><span>MwSt. ({taxRate}%)</span><span>{fmtEur(tax)}</span></div>
            <div className="total-row total-final"><span>Gesamt</span><span>{fmtEur(total)}</span></div>
          </div>

          <div className="form-group mt-16">
            <label>Einleitungstext (optional)</label>
            <textarea value={introText} onChange={(e) => setIntroText(e.target.value)} rows={2} placeholder="Sehr geehrte/r ..." />
          </div>
          <div className="form-group">
            <label>Schlusstext (optional)</label>
            <textarea value={outroText} onChange={(e) => setOutroText(e.target.value)} rows={2} placeholder="Bei Fragen stehe ich Ihnen gerne zur Verfügung." />
          </div>

          <div className="modal-footer">
            <button type="button" className="btn btn-secondary" onClick={onClose}>Abbrechen</button>
            <button type="submit" className="btn btn-primary" disabled={saving}>
              {saving ? 'Speichern...' : isEdit ? 'Rechnung speichern' : 'Rechnung erstellen'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// --- Dunning Modal ---

function DunningModal({ invoice, onClose, onSaved }: { invoice: Invoice; onClose: () => void; onSaved: () => void }) {
  const { showToast } = useToast();
  const currentLevel = invoice.dunning_level || 0;
  const nextLevelKey = dunningLevelKeys[currentLevel] || '';
  const [level, setLevel] = useState(nextLevelKey);
  const [sendEmail, setSendEmail] = useState(true);
  const [customMessage, setCustomMessage] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const paid = invoice.total_paid || 0;
  const remaining = Math.max(0, invoice.total_eur - paid);

  const fmt = (n: number) => n.toLocaleString('de-DE', { style: 'currency', currency: 'EUR' });

  const handleSubmit = async () => {
    if (!level) return;
    setSubmitting(true);
    try {
      await invoicesApi.sendDunning(invoice.id, {
        level,
        send_email: sendEmail,
        custom_message: customMessage || undefined,
      });
      showToast(`${dunningLevelLabels[level] || level} erstellt`, 'success');
      onSaved();
      onClose();
    } catch (err: any) {
      const detail = err.response?.data?.detail;
      showToast(typeof detail === 'string' ? detail : 'Fehler beim Erstellen der Mahnung', 'error');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 520 }}>
        <div className="flex-between mb-16">
          <h2>Mahnung erstellen</h2>
          <button className="btn btn-sm btn-secondary" onClick={onClose}><X size={16} /></button>
        </div>

        <div className="card mb-16" style={{ padding: 16, background: 'var(--bg)' }}>
          <div className="text-sm text-muted mb-8">Rechnung {invoice.invoice_number}</div>
          <div className="flex-between">
            <span>Gesamtbetrag: <strong>{fmt(invoice.total_eur)}</strong></span>
            <span>Restbetrag: <strong style={{ color: 'var(--danger)' }}>{fmt(remaining)}</strong></span>
          </div>
          {currentLevel > 0 && (
            <div className="text-sm text-muted mt-8">
              Aktuelle Mahnstufe: {currentLevel === 1 ? 'Erinnerung' : `${currentLevel}. Mahnung`}
            </div>
          )}
        </div>

        <div className="form-group mb-16">
          <label>Mahnstufe</label>
          <select value={level} onChange={(e) => setLevel(e.target.value)}>
            {dunningLevelKeys.filter((_, i) => i >= currentLevel).map((key) => (
              <option key={key} value={key}>{dunningLevelLabels[key]}</option>
            ))}
          </select>
        </div>

        <div className="form-group mb-16">
          <label style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <input type="checkbox" checked={sendEmail} onChange={(e) => setSendEmail(e.target.checked)} />
            Per E-Mail senden
          </label>
        </div>

        <div className="form-group mb-16">
          <label>Eigene Nachricht (optional)</label>
          <textarea
            value={customMessage}
            onChange={(e) => setCustomMessage(e.target.value)}
            placeholder="Leer lassen für Standard-Vorlage..."
            rows={4}
          />
        </div>

        <div className="flex gap-8" style={{ justifyContent: 'flex-end' }}>
          <button className="btn btn-secondary" onClick={onClose}>Abbrechen</button>
          <button className="btn btn-primary" onClick={handleSubmit} disabled={submitting || !level}>
            {submitting ? 'Wird erstellt...' : `${dunningLevelLabels[level] || 'Mahnung'} erstellen`}
          </button>
        </div>
      </div>
    </div>
  );
}

// --- Invoice Preview (sevDesk-Style) ---

function InvoicePreview({
  invoice,
  profile,
  clientName,
  client,
  onClose,
  onSend,
  onPayment,
  onCancel,
  onEdit,
  onDelete,
  onReminder,
  onStripeCheckout,
}: {
  invoice: Invoice;
  profile: CoachProfile;
  clientName: string | null;
  client: Client | null;
  onClose: () => void;
  onSend?: (id: string) => void;
  onPayment?: (inv: Invoice) => void;
  onCancel?: (inv: Invoice) => void;
  onEdit?: (inv: Invoice) => void;
  onStripeCheckout?: (inv: Invoice) => void;
  onDelete?: (id: string) => void;
  onReminder?: (id: string) => void;
}) {
  const { showToast } = useToast();
  const [audit, setAudit] = useState<AuditEntry[]>([]);
  const [showAudit, setShowAudit] = useState(false);

  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    invoicesApi.audit(invoice.id).then((res) => setAudit(res.data)).catch(() => {});
  }, [invoice.id]);

  const handleDownloadPdf = async () => {
    setDownloading(true);
    try {
      const res = await invoicesApi.downloadPdf(invoice.id, true);
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${invoice.invoice_number}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      showToast('PDF-Download fehlgeschlagen', 'error');
    } finally {
      setDownloading(false);
    }
  };

  const fmt = (n: number) => n.toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  const fmtDate = (d: string) => new Date(d).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' });
  const fmtDateTime = (d: string) => new Date(d).toLocaleString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });

  const coachName = profile.business_name || `${profile.first_name} ${profile.last_name}`;
  const hasAddress = profile.address_line1 || profile.zip_code;
  const hasBankDetails = profile.iban;
  const hasTax = profile.tax_number || profile.vat_id;

  // Build compact sender line (like envelope window)
  const senderParts = [coachName];
  if (profile.address_line1) senderParts.push(profile.address_line1);
  if (profile.zip_code || profile.city) senderParts.push(`${profile.zip_code || ''} ${profile.city || ''}`.trim());
  const senderLine = senderParts.join(' · ');

  const handlePrint = () => {
    const printContent = document.getElementById('invoice-print-area');
    if (!printContent) return;
    const w = window.open('', '_blank');
    if (!w) return;
    const tc = getThemeColors();
    w.document.write(`<!DOCTYPE html><html><head><title>Rechnung ${invoice.invoice_number}</title>
<style>
@page { margin: 20mm 15mm 25mm 20mm; size: A4; }
* { margin: 0; padding: 0; box-sizing: border-box; }
body { font-family: 'Helvetica Neue', Helvetica, Arial, sans-serif; font-size: 9.5pt; color: #333; line-height: 1.5; }

.inv-accent { height: 4px; background: linear-gradient(90deg, ${tc.primary}, ${tc.primaryDark}); margin-bottom: 30px; }

.inv-header { display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 8px; }
.inv-logo-area { }
.inv-logo-area .company-name { font-size: 16pt; font-weight: 700; color: ${tc.primary}; letter-spacing: 0.5px; }
.inv-logo-area .company-subtitle { font-size: 8.5pt; color: #888; margin-top: 2px; }
.inv-badge { display: inline-block; padding: 4px 14px; border-radius: 4px; font-size: 8pt; font-weight: 600; letter-spacing: 1.5px; text-transform: uppercase; }
.inv-badge-rechnung { background: ${tc.badgeBg}; color: ${tc.primary}; border: 1px solid ${tc.badgeBorder}; }
.inv-badge-storno { background: #fef2f2; color: #dc2626; border: 1px solid #fca5a5; }

.inv-sender-line { font-size: 7pt; color: #999; letter-spacing: 0.3px; border-bottom: 1px solid #e5e5e5; padding-bottom: 4px; margin-bottom: 8px; margin-top: 28px; }

.inv-address-meta { display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 32px; gap: 40px; }
.inv-recipient { flex: 1; }
.inv-recipient .name { font-size: 10.5pt; font-weight: 600; color: #222; }
.inv-recipient .address-line { font-size: 9.5pt; color: #555; line-height: 1.7; }

.inv-details { width: 220px; flex-shrink: 0; }
.inv-details table { width: 100%; border-collapse: collapse; }
.inv-details td { padding: 3px 0; font-size: 9pt; vertical-align: top; }
.inv-details td:first-child { color: #888; padding-right: 12px; white-space: nowrap; }
.inv-details td:last-child { color: #333; font-weight: 500; text-align: right; }

.inv-subject { margin-bottom: 20px; font-size: 10pt; }
.inv-subject strong { color: ${tc.primary}; }
.inv-greeting { font-size: 9.5pt; color: #555; margin-bottom: 16px; }

.inv-table { width: 100%; border-collapse: collapse; margin-bottom: 4px; }
.inv-table thead { background: ${tc.headBg}; }
.inv-table th { padding: 10px 12px; text-align: left; font-size: 8pt; font-weight: 600; text-transform: uppercase; letter-spacing: 0.6px; color: ${tc.primary}; border-bottom: 2px solid ${tc.border}; }
.inv-table th.right { text-align: right; }
.inv-table td { padding: 11px 12px; font-size: 9.5pt; border-bottom: 1px solid #f0ece6; color: #444; }
.inv-table td.right { text-align: right; font-variant-numeric: tabular-nums; }
.inv-table td.pos { color: #999; width: 36px; }
.inv-table tbody tr:last-child td { border-bottom: 2px solid ${tc.border}; }

.inv-totals { display: flex; justify-content: flex-end; margin-bottom: 24px; }
.inv-totals-inner { width: 260px; }
.inv-totals-row { display: flex; justify-content: space-between; padding: 5px 12px; font-size: 9.5pt; color: #666; }
.inv-totals-row.sub { border-top: 1px solid #e5e5e5; margin-top: 4px; padding-top: 8px; }
.inv-totals-row.final { background: ${tc.primary}; color: white; font-weight: 700; font-size: 11pt; padding: 10px 12px; border-radius: 4px; margin-top: 6px; }

.inv-payment-info { background: ${tc.headBg}; border-radius: 6px; padding: 14px 16px; margin-bottom: 24px; font-size: 9pt; color: #666; }
.inv-payment-info strong { color: ${tc.primary}; }

.inv-cancel-info { background: #fef2f2; border: 1px solid #fca5a5; border-radius: 6px; padding: 12px 16px; margin-bottom: 20px; font-size: 9pt; color: #991b1b; }

.inv-footer { border-top: 2px solid ${tc.border}; padding-top: 16px; margin-top: 40px; }
.inv-footer-grid { display: grid; grid-template-columns: 1fr 1fr 1fr; gap: 24px; font-size: 8pt; line-height: 1.8; color: #777; }
.inv-footer-grid .col-title { font-size: 7.5pt; font-weight: 700; text-transform: uppercase; letter-spacing: 0.8px; color: ${tc.primary}; margin-bottom: 4px; }
.inv-footer-note { text-align: center; margin-top: 16px; font-size: 7.5pt; color: #bbb; letter-spacing: 0.3px; }

@media print {
  body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
  .inv-table thead { background: ${tc.headBg} !important; }
  .inv-totals-row.final { background: ${tc.primary} !important; color: white !important; }
}
</style></head><body>${printContent.innerHTML}</body></html>`);
    w.document.close();
    w.focus();
    setTimeout(() => { w.print(); w.close(); }, 400);
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="invoice-preview-modal" onClick={(e) => e.stopPropagation()}>
        <div className="invoice-preview-toolbar">
          <div>
            <h3 style={{ marginBottom: 2 }}>Rechnungsvorschau</h3>
            <span className="text-sm text-muted">
              {invoice.invoice_number}
              {invoice.is_locked && (
                <span style={{ marginLeft: 8, color: 'var(--text-muted)' }}>
                  <Lock size={12} style={{ verticalAlign: 'middle' }} /> Gesperrt (GoBD)
                </span>
              )}
            </span>
          </div>
          <div className="flex gap-8">
            {/* Status-Aktionen */}
            {invoice.status === 'draft' && onEdit && (
              <button className="btn btn-secondary btn-sm" data-tip="Rechnung bearbeiten" onClick={() => { onClose(); onEdit(invoice); }}>
                <Edit3 size={14} /> Bearbeiten
              </button>
            )}
            {invoice.status === 'draft' && onSend && (
              <button className="btn btn-accent btn-sm" data-tip="Rechnung versenden und für Bearbeitung sperren (GoBD)" onClick={() => { onClose(); onSend(invoice.id); }}>
                <Send size={14} /> Senden & Sperren
              </button>
            )}
            {['sent', 'partially_paid', 'overdue'].includes(invoice.status) && onPayment && (
              <button className="btn btn-accent btn-sm" data-tip="Eingehende Zahlung erfassen" onClick={() => { onClose(); onPayment(invoice); }}>
                <CreditCard size={14} /> Zahlung erfassen
              </button>
            )}
            {['sent', 'partially_paid', 'overdue'].includes(invoice.status) && onReminder && (
              <button className="btn btn-secondary btn-sm" data-tip="Zahlungserinnerung per E-Mail senden" onClick={() => { onClose(); onReminder(invoice.id); }}>
                <Bell size={14} /> Mahnen
              </button>
            )}
            {['sent', 'partially_paid', 'overdue', 'paid'].includes(invoice.status) && !invoice.is_cancellation && !invoice.cancelled_by_id && onCancel && (
              <button className="btn btn-danger btn-sm" data-tip="Stornorechnung erstellen (GoBD-konform)" onClick={() => { onClose(); onCancel(invoice); }}>
                <Ban size={14} /> Stornieren
              </button>
            )}
            {invoice.status === 'draft' && onDelete && (
              <button className="btn btn-danger btn-sm" data-tip="Entwurf unwiderruflich löschen" onClick={() => { onClose(); onDelete(invoice.id); }}>
                <Trash2 size={14} /> Löschen
              </button>
            )}
            {/* Dokument-Aktionen */}
            {audit.length > 0 && (
              <button className="btn btn-secondary btn-sm" data-tip="Änderungsverlauf anzeigen" onClick={() => setShowAudit(!showAudit)}>
                <History size={14} /> Verlauf
              </button>
            )}
            <button className="btn btn-primary btn-sm" data-tip="PDF mit ZUGFeRD-XML herunterladen" onClick={handleDownloadPdf} disabled={downloading}>
              <FileDown size={14} /> {downloading ? 'Laden...' : 'ZUGFeRD-PDF'}
            </button>
            <button className="btn btn-secondary btn-sm" data-tip="Druckansicht öffnen" onClick={handlePrint}>
              <Printer size={14} /> Drucken
            </button>
            <button className="btn-icon" data-tip="Schließen" onClick={onClose}><X size={20} /></button>
          </div>
        </div>

        {/* Audit Trail Panel */}
        {showAudit && audit.length > 0 && (
          <div className="audit-panel">
            <h4 style={{ marginBottom: 8, color: 'var(--primary-dark)', fontSize: '0.85rem' }}>
              <Clock size={14} style={{ verticalAlign: 'middle' }} /> Änderungsverlauf
            </h4>
            {audit.map((entry) => (
              <div key={entry.id} className="audit-entry">
                <span className="audit-time">{fmtDateTime(entry.timestamp)}</span>
                <span className="audit-desc">{entry.description || entry.action}</span>
              </div>
            ))}
          </div>
        )}

        {/* Mahnverlauf Panel */}
        {invoice.dunning_entries && invoice.dunning_entries.length > 0 && (
          <div className="audit-panel" style={{ borderLeft: '3px solid var(--danger)' }}>
            <h4 style={{ marginBottom: 8, color: 'var(--danger)', fontSize: '0.85rem' }}>
              <AlertTriangle size={14} style={{ verticalAlign: 'middle' }} /> Mahnverlauf ({invoice.dunning_entries.length})
            </h4>
            {[...invoice.dunning_entries].sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime()).map((de) => (
              <div key={de.id} className="audit-entry" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8 }}>
                <div>
                  <span className="audit-time">{fmtDateTime(de.created_at)}</span>
                  <span className="audit-desc" style={{ fontWeight: 600 }}>
                    {dunningLevelLabels[de.level] || de.level}
                  </span>
                  {de.sent_at && <span className="audit-desc" style={{ color: 'var(--success)', marginLeft: 6, fontSize: '0.7rem' }}>E-Mail gesendet</span>}
                  {de.deadline && <span className="audit-desc" style={{ marginLeft: 6, fontSize: '0.7rem' }}>Frist: {fmtDate(de.deadline)}</span>}
                </div>
                {de.fee > 0 && (
                  <span style={{ fontSize: '0.75rem', fontWeight: 600, color: 'var(--danger)', whiteSpace: 'nowrap' }}>
                    +{de.fee.toLocaleString('de-DE', { minimumFractionDigits: 2 })} EUR
                  </span>
                )}
              </div>
            ))}
          </div>
        )}

        <div className="invoice-preview-content" id="invoice-print-area">
          {/* Accent Bar */}
          <div className="inv-accent" />

          {/* Header: Company name + Invoice badge */}
          <div className="inv-header">
            <div className="inv-logo-area">
              <div className="company-name">{coachName}</div>
              {profile.title && <div className="company-subtitle">{profile.title}</div>}
            </div>
            <div>
              <span className={`inv-badge ${invoice.is_cancellation ? 'inv-badge-storno' : 'inv-badge-rechnung'}`}>
                {invoice.is_cancellation ? 'Stornorechnung' : 'Rechnung'}
              </span>
            </div>
          </div>

          {/* Sender line (compact, like envelope window) */}
          <div className="inv-sender-line">{senderLine}</div>

          {/* Address + Meta Details side by side */}
          <div className="inv-address-meta">
            <div className="inv-recipient">
              {clientName ? (
                <>
                  <div className="name">{clientName}</div>
                  {client?.address_line1 && <div className="address-line">{client.address_line1}</div>}
                  {(client?.zip_code || client?.city) && (
                    <div className="address-line">{client?.zip_code} {client?.city}</div>
                  )}
                  {client?.email && <div className="address-line">{client.email}</div>}
                </>
              ) : (
                <div className="name" style={{ color: '#999', fontStyle: 'italic' }}>Kein Empfänger zugeordnet</div>
              )}
            </div>

            <div className="inv-details">
              <table>
                <tbody>
                  <tr><td>Rechnungsnr.</td><td>{invoice.invoice_number}</td></tr>
                  <tr><td>Rechnungsdatum</td><td>{fmtDate(invoice.invoice_date)}</td></tr>
                  <tr><td>Fällig bis</td><td>{fmtDate(invoice.due_date)}</td></tr>
                  {invoice.service_date && (
                    <tr><td>Leistungszeitraum</td><td>{invoice.service_date}</td></tr>
                  )}
                  {hasTax && (
                    <>
                      {profile.tax_number && <tr><td>Steuernr.</td><td>{profile.tax_number}</td></tr>}
                      {profile.vat_id && <tr><td>USt-IdNr.</td><td>{profile.vat_id}</td></tr>}
                    </>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* Cancellation info */}
          {invoice.is_cancellation && invoice.cancellation_reason && (
            <div className="inv-cancel-info">
              Stornorechnung zu Originalrechnung. Grund: {invoice.cancellation_reason}
            </div>
          )}
          {invoice.status === 'cancelled' && invoice.cancellation_reason && !invoice.is_cancellation && (
            <div className="inv-cancel-info">
              Diese Rechnung wurde storniert. Grund: {invoice.cancellation_reason}
            </div>
          )}

          {/* Subject */}
          {invoice.subject && (
            <div className="inv-subject">
              <strong>Betreff:</strong> {invoice.subject}
            </div>
          )}

          {/* Package reference */}
          {invoice.package_name && (
            <div style={{ fontSize: '9pt', color: '#777', marginBottom: 8 }}>
              {invoice.installment_number
                ? `Rate ${invoice.installment_number} — Coaching-Paket: ${invoice.package_name}`
                : `Gemäß Coaching-Paket: ${invoice.package_name}`
              }
            </div>
          )}

          {/* Greeting / Intro text */}
          <div className="inv-greeting" style={{ whiteSpace: 'pre-line' }}>
            {invoice.intro_text || (
              <>
                {clientName
                  ? `Sehr ${clientName.startsWith('Frau') ? 'geehrte' : 'geehrte/r'} ${clientName},`
                  : 'Sehr geehrte Damen und Herren,'
                }{'\n'}
                für die erbrachten Leistungen erlaube ich mir, folgende Positionen in Rechnung zu stellen:
              </>
            )}
          </div>

          {/* Items Table */}
          <table className="inv-table">
            <thead>
              <tr>
                <th>Pos.</th>
                <th>Beschreibung</th>
                <th className="right">Menge</th>
                <th className="right">Einheit</th>
                <th className="right">Einzelpreis</th>
                <th className="right">Gesamt</th>
              </tr>
            </thead>
            <tbody>
              {invoice.items.map((item, i) => (
                <tr key={item.id}>
                  <td className="pos">{i + 1}</td>
                  <td>{item.description}</td>
                  <td className="right">{item.quantity % 1 === 0 ? item.quantity : fmt(item.quantity)}</td>
                  <td className="right">{unitLabels[item.unit] || item.unit}</td>
                  <td className="right">{fmt(Math.abs(item.unit_price_eur))} &euro;</td>
                  <td className="right" style={item.total_eur < 0 ? { color: '#dc2626' } : undefined}>
                    {fmt(item.total_eur)} &euro;
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {/* Totals */}
          <div className="inv-totals">
            <div className="inv-totals-inner">
              <div className="inv-totals-row">
                <span>Zwischensumme (netto)</span>
                <span>{fmt(invoice.subtotal_eur)} &euro;</span>
              </div>
              {invoice.tax_rate_percent > 0 ? (
                <div className="inv-totals-row sub">
                  <span>USt. {fmt(invoice.tax_rate_percent)}%</span>
                  <span>{fmt(invoice.tax_amount_eur)} &euro;</span>
                </div>
              ) : profile.is_kleinunternehmer ? (
                <div className="inv-totals-row sub" style={{ fontSize: '8pt', color: '#999' }}>
                  <span>Kein USt.-Ausweis (Kleinunternehmer gem. § 19 UStG)</span>
                </div>
              ) : (
                <div className="inv-totals-row sub">
                  <span>USt. 0,00%</span>
                  <span>0,00 &euro;</span>
                </div>
              )}
              <div className="inv-totals-row final">
                <span>Rechnungsbetrag</span>
                <span>{fmt(invoice.total_eur)} &euro;</span>
              </div>
            </div>
          </div>

          {/* Outro text */}
          {invoice.outro_text && (
            <div className="inv-outro" style={{ fontSize: '9.5pt', color: '#555', marginBottom: 24, whiteSpace: 'pre-line' }}>
              {invoice.outro_text}
            </div>
          )}

          {/* Payment terms */}
          {invoice.payment_terms && (
            <div className="inv-payment-info">
              <strong>Zahlungsbedingungen:</strong> {invoice.payment_terms}
              {hasBankDetails && (
                <span> Bitte überweisen Sie den Betrag auf das unten angegebene Konto unter Angabe der Rechnungsnummer <strong>{invoice.invoice_number}</strong>.</span>
              )}
            </div>
          )}

          {/* Footer */}
          <div className="inv-footer">
            <div className="inv-footer-grid">
              <div>
                <div className="col-title">Kontakt</div>
                <div>{coachName}</div>
                {hasAddress && (
                  <>
                    {profile.address_line1 && <div>{profile.address_line1}</div>}
                    {profile.address_line2 && <div>{profile.address_line2}</div>}
                    {(profile.zip_code || profile.city) && <div>{profile.zip_code} {profile.city}</div>}
                  </>
                )}
                <div>{profile.email}</div>
                {profile.phone && <div>Tel. {profile.phone}</div>}
              </div>
              <div>
                <div className="col-title">Bankverbindung</div>
                {hasBankDetails ? (
                  <>
                    {profile.bank_name && <div>{profile.bank_name}</div>}
                    <div>IBAN: {profile.iban}</div>
                    {profile.bic && <div>BIC: {profile.bic}</div>}
                  </>
                ) : (
                  <div style={{ color: '#ccc', fontStyle: 'italic' }}>Unter Einstellungen hinterlegen</div>
                )}
              </div>
              <div>
                <div className="col-title">Steuerdaten</div>
                {hasTax ? (
                  <>
                    {profile.tax_number && <div>Steuernr.: {profile.tax_number}</div>}
                    {profile.vat_id && <div>USt-IdNr.: {profile.vat_id}</div>}
                  </>
                ) : (
                  <div style={{ color: '#ccc', fontStyle: 'italic' }}>Unter Einstellungen hinterlegen</div>
                )}
              </div>
            </div>
            <div className="inv-footer-note">
              Erstellt mit CoachDesk365
            </div>
          </div>

          {/* Warnings for missing data (only visible in preview, not in print) */}
          {(!hasAddress || !hasBankDetails || !hasTax) && (
            <div className="inv-hint-setup" style={{
              marginTop: 20, padding: 14, background: '#fff8ee',
              border: '1px solid #f0d9a8', borderRadius: 6, fontSize: '9pt', color: '#92600e',
            }}>
              Hinweis: Bitte hinterlege deine vollständigen Geschäftsdaten (Adresse, Steuernummer, Bankverbindung)
              unter <strong>Einstellungen</strong>, damit sie auf der Rechnung erscheinen.
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// --- Cancellation Form ---

function CancelForm({ invoice, onClose, onSaved }: { invoice: Invoice; onClose: () => void; onSaved: () => void }) {
  const { showToast } = useToast();
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const fmt = (n: number) => n.toLocaleString('de-DE', { style: 'currency', currency: 'EUR' });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    try {
      await invoicesApi.cancel(invoice.id, { reason: reason || 'Storniert' });
      showToast('Rechnung storniert — Stornorechnung erstellt', 'success');
      await onSaved();
      onClose();
    } catch (err: any) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : 'Stornierung fehlgeschlagen');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <div>
            <h2>Rechnung stornieren</h2>
            <span className="text-sm text-muted">{invoice.invoice_number} — {fmt(invoice.total_eur)}</span>
          </div>
          <button className="btn-icon" onClick={onClose}><X size={20} /></button>
        </div>
        {error && <div className="auth-error" style={{ margin: '0 24px' }}>{error}</div>}
        <form onSubmit={handleSubmit}>
          <div style={{ padding: '0 24px 8px', fontSize: '0.88rem', color: 'var(--text-light)' }}>
            Es wird eine <strong>Stornorechnung</strong> (Gutschrift) erstellt. Die Originalrechnung bleibt
            erhalten und wird als storniert markiert. Dies entspricht den GoBD-Anforderungen.
          </div>
          <div className="form-group" style={{ padding: '0 24px' }}>
            <label>Grund der Stornierung</label>
            <textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="z.B. Leistung nicht erbracht, Kundenreklamation..."
              rows={3}
            />
          </div>
          <div className="modal-footer" style={{ padding: '16px 24px 24px' }}>
            <button type="button" className="btn btn-secondary" onClick={onClose}>Abbrechen</button>
            <button type="submit" className="btn btn-danger" disabled={saving}>
              <Ban size={16} /> {saving ? 'Stornieren...' : 'Rechnung stornieren'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// --- Payment Form ---

function PaymentForm({ invoice, onClose, onSaved, onStripe }: { invoice: Invoice; onClose: () => void; onSaved: () => void; onStripe?: (inv: Invoice) => void }) {
  const { showToast } = useToast();
  const [method, setMethod] = useState('bank_transfer');
  const [amount, setAmount] = useState('');
  const [reference, setReference] = useState('');
  const [paymentDate, setPaymentDate] = useState(new Date().toISOString().split('T')[0]);
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);
  const [totalPaid, setTotalPaid] = useState(0);
  const [loadingPayments, setLoadingPayments] = useState(true);
  const [stripeAvailable, setStripeAvailable] = useState(false);

  useEffect(() => {
    invoicesApi.listPayments(invoice.id).then((res) => {
      const paid = (res.data || []).reduce((sum: number, p: any) => sum + (p.amount_eur || 0), 0);
      setTotalPaid(paid);
      const remaining = Math.max(invoice.total_eur - paid, 0);
      setAmount(remaining.toFixed(2));
    }).catch(() => {
      setAmount(invoice.total_eur.toFixed(2));
    }).finally(() => setLoadingPayments(false));
    // Stripe-Verfügbarkeit prüfen
    paymentsApi.getStatus(invoice.id).then((res) => {
      setStripeAvailable(res.data.stripe_configured);
    }).catch(() => {});
  }, [invoice.id, invoice.total_eur]);

  const remaining = Math.max(invoice.total_eur - totalPaid, 0);
  const paidPct = invoice.total_eur > 0 ? Math.min(100, Math.round(totalPaid / invoice.total_eur * 100)) : 0;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    try {
      await invoicesApi.addPayment(invoice.id, {
        payment_method: method,
        amount_eur: parseFloat(amount),
        reference: reference || undefined,
        payment_date: paymentDate,
        notes: note || undefined,
      });
      showToast('Zahlung erfasst', 'success');
      await onSaved();
      onClose();
    } catch (err: any) {
      showToast(err.response?.data?.detail || 'Fehler beim Erfassen der Zahlung', 'error');
    } finally {
      setSaving(false);
    }
  };

  const fmt = (n: number) => n.toLocaleString('de-DE', { style: 'currency', currency: 'EUR' });

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <div>
            <h2>Zahlung erfassen</h2>
            <span className="text-sm text-muted">
              {invoice.invoice_number} — Gesamt: {fmt(invoice.total_eur)}
            </span>
          </div>
          <button className="btn-icon" onClick={onClose}><X size={20} /></button>
        </div>

        {/* Fortschrittsbalken */}
        {!loadingPayments && (
          <div style={{ padding: '0 24px 16px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.85rem', marginBottom: 4 }}>
              <span>{fmt(totalPaid)} von {fmt(invoice.total_eur)} bezahlt</span>
              <span style={{ fontWeight: 600 }}>{paidPct}%</span>
            </div>
            <div style={{ height: 8, background: '#eee', borderRadius: 4, overflow: 'hidden' }}>
              <div style={{
                height: '100%',
                width: `${paidPct}%`,
                background: paidPct >= 100 ? 'var(--success)' : 'var(--primary)',
                borderRadius: 4,
                transition: 'width 0.3s',
              }} />
            </div>
            {remaining > 0 && (
              <div className="text-sm text-muted" style={{ marginTop: 4 }}>
                Offen: {fmt(remaining)}
              </div>
            )}
          </div>
        )}

        <form onSubmit={handleSubmit}>
          <div className="form-row">
            <div className="form-group">
              <label>Zahlungsart</label>
              <select value={method} onChange={(e) => setMethod(e.target.value)}>
                <option value="bank_transfer">Überweisung</option>
                <option value="cash">Bar</option>
                <option value="paypal">PayPal</option>
                <option value="sepa">SEPA-Lastschrift</option>
                <option value="stripe">Kreditkarte</option>
                <option value="klarna">Klarna</option>
              </select>
            </div>
            <div className="form-group">
              <label>Betrag (€)</label>
              <input type="number" value={amount} onChange={(e) => setAmount(e.target.value)} min={0.01} step={0.01} required />
            </div>
          </div>
          <div className="form-row">
            <div className="form-group">
              <label>Zahlungsdatum</label>
              <input type="date" value={paymentDate} onChange={(e) => setPaymentDate(e.target.value)} required />
            </div>
            <div className="form-group">
              <label>Referenz / Verwendungszweck</label>
              <input value={reference} onChange={(e) => setReference(e.target.value)} placeholder="Optional" />
            </div>
          </div>
          <div className="form-group">
            <label>Notiz</label>
            <textarea value={note} onChange={(e) => setNote(e.target.value)} rows={2} placeholder="Optional" />
          </div>
          <div className="modal-footer" style={{ justifyContent: 'space-between' }}>
            <div>
              {stripeAvailable && onStripe && remaining > 0 && (
                <button type="button" className="btn btn-primary btn-sm" data-tip="Online-Zahlung via Stripe (Kreditkarte etc.)" onClick={() => onStripe(invoice)}>
                  <CreditCard size={14} /> Online bezahlen
                </button>
              )}
            </div>
            <div className="flex gap-8">
              <button type="button" className="btn btn-secondary" onClick={onClose}>Abbrechen</button>
              <button type="submit" className="btn btn-accent" disabled={saving}>
                {saving ? 'Speichern...' : 'Zahlung erfassen'}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}
